import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import DocumentsTab from '../components/DocumentsTab';
import {
  Package, Plus, Search, Upload, Download,
  Edit, Trash2, Clock, Tag, CheckCircle, AlertCircle, XCircle,
  Wrench, ShoppingCart, X, ChevronDown, ChevronUp, Check,
  Building2, ExternalLink, Lock, CreditCard, ArrowUpRight, Hash,
  Layers, ArrowRightLeft, MapPin, Paperclip, FileText, Pencil, SlidersHorizontal, User,
  Archive, Users, Truck, Gauge, Star
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';
import { SidePanel } from '../components/SidePanel';
import { MaterielCaracTab } from '../components/CaracTab';
import { useSociete } from '../context/SocieteContext';
import { useAuth } from '../context/AuthContext';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';
import { PriceInput } from '../components/PriceInput';
import { TokenizedSearchBar } from '../components/TokenizedSearchBar';
import MatBadges from '../components/MatBadges';
import { ActivitesFichePanel, useHasActivite, ActivityPulse } from '../components/ActivitesFichePanel';

/** Normalise une chaîne : retire les accents et met en minuscules */
function normalizeStr(str) {
  return (str ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

const STATUTS = {
  disponible: { label: 'Disponible', color: '#22C55E', bg: '#F0FDF4', icon: CheckCircle },
  loue: { label: 'Loué', color: 'var(--primary-light)', bg: 'var(--primary-50)', icon: Package },
  vendu: { label: 'Vendu', color: '#7C3AED', bg: '#F5F3FF', icon: ShoppingCart },
  hors_service: { label: 'Hors service', color: '#DC2626', bg: '#FEF2F2', icon: XCircle },
};

const PARCS = {
  location_courte_duree: { label: 'Location C.D.', color: '#1D4ED8', bg: '#EFF6FF', border: '#BFDBFE' },
  client:                { label: 'Client',         color: '#15803D', bg: '#F0FDF4', border: '#BBF7D0' },
  stock:                 { label: 'Stock',           color: '#475569', bg: '#F1F5F9', border: '#CBD5E1' },
  vehicule_de_societe:   { label: 'Véh. société',   color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' },
  epave:                 { label: 'Épave',           color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
};

const BADGE_OPTIONS = [
  { value: 'operationnel', label: 'Opérationnel', dot: '#22C55E' },
  { value: 'entretien',    label: 'En entretien',  dot: '#3B82F6' },
  { value: 'panne',        label: 'En panne',      dot: '#F97316' },
  { value: 'urgent',       label: 'Urgent',        dot: '#DC2626' },
  { value: 'loue',         label: 'Loué',          dot: '#166534' },
  { value: 'non_conforme', label: 'Non conforme',  dot: '#F59E0B' },
  { value: 'reforme',      label: 'Réformé',       dot: '#94A3B8' },
];

// ─── Column Visibility ────────────────────────────────────────────────────────
const COLUMN_DEFS = [
  { key: 'numero_parc',       label: 'N° Série / Immat. / N° Parc', locked: true,  width: 13 },
  { key: 'article',           label: 'Article',                     locked: false, width: 8 },
  { key: 'nom',               label: 'Nom / Marque / Modèle',       locked: false, width: 21 },
  { key: 'annee',             label: 'Année',                       locked: false, width: 5 },
  { key: 'badges',            label: 'État',                        locked: false, width: 7 },
  { key: 'parc',              label: 'Parc',                        locked: false, width: 6 },
  { key: 'compteur',          label: 'Compteur',                    locked: false, width: 6 },
  { key: 'proprietaire',      label: 'Exploitant / Propriétaire',   locked: false, width: 18 },
  { key: 'dernier_comptage',  label: 'Dernier Comptage',            locked: false, width: 7 },
];
const DEFAULT_COLS = Object.fromEntries(COLUMN_DEFS.map(c => [c.key, true]));
DEFAULT_COLS.compteur = false;
DEFAULT_COLS.dernier_comptage = false;

// ─── Colonnes disponibles dans la Tokenized Search Bar ───────────────────────
const TOKEN_COLUMNS = [
  { key: 'general',         label: 'Général',         color: '#6B7280' },
  { key: 'numero_parc',     label: 'N° Parc',          color: '#2563EB' },
  { key: 'nom',             label: 'Nom',              color: '#D97706' },
  { key: 'marque',          label: 'Marque',           color: '#EA580C' },
  { key: 'modele',          label: 'Modèle',           color: '#7C3AED' },
  { key: 'numero_serie',    label: 'N° Série',         color: '#0891B2' },
  { key: 'immatriculation', label: 'Immatriculation',  color: '#0891B2' },
  { key: 'annee',           label: 'Année',            color: '#9333EA' },
  { key: 'proprietaire',    label: 'Propriétaire',     color: '#059669' },
  { key: 'exploitant',      label: 'Exploitant',       color: '#0D9488' },
];

// ─── Construit les params API depuis les jetons de recherche ─────────────────
function buildParamsFromTokens(tokens) {
  const params = {};
  tokens.forEach(t => {
    if (!t.value.trim()) return;
    switch (t.column) {
      case 'general':          params.q                 = (params.q ? params.q + ' ' : '') + t.value; break;
      case 'numero_parc':      params.numero_parc_q     = t.value; break;
      case 'nom':              params.nom_q             = t.value; break;
      case 'marque':           params.marque_nom_q      = t.value; break;
      case 'modele':           params.modele_q          = t.value; break;
      case 'numero_serie':     params.numero_serie_q    = t.value; break;
      case 'immatriculation':  params.immatriculation_q = t.value; break;
      case 'annee':            params.annee_q           = t.value; break;
      case 'proprietaire':     params.proprietaire_q    = t.value; break;
      case 'exploitant':       params.exploitant_q      = t.value; break;
      default: break;
    }
  });
  return params;
}
const COLS_KEY = 'materiels_visible_cols_v1';
function loadSavedCols() {
  try {
    const saved = JSON.parse(localStorage.getItem(COLS_KEY) || 'null');
    if (saved && typeof saved === 'object') return { ...DEFAULT_COLS, ...saved, numero_parc: true };
  } catch {}
  return { ...DEFAULT_COLS };
}

function StatCard({ title, value, color, icon: Icon, onClick, active }) {
  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl p-4 border transition-all"
      style={{
        borderColor: active ? color : 'var(--border)',
        boxShadow: active ? `0 0 0 2px ${color}40` : 'none',
        cursor: onClick ? 'pointer' : 'default',
        background: active ? `${color}0D` : 'white',
      }}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
          <Icon size={18} style={{ color }} />
        </div>
        <div>
          <p className="text-2xl font-bold" style={{ color: 'var(--text)',  }}>{value}</p>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{title}</p>
        </div>
      </div>
    </div>
  );
}

// ─── ComboDropdown ────────────────────────────────────────────────────────────
// options: [{id, label}] | value: selected id | valueLabel: selected display name
function ComboDropdown({ value, valueLabel, options = [], placeholder, onCreate, onChange, disabled, 'data-testid': testId }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const filtered = search.length > 0
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;
  const exactMatch = options.some(o => o.label.toLowerCase() === search.trim().toLowerCase());

  const handleSelect = (opt) => { onChange(opt.id, opt.label); setSearch(''); setOpen(false); };
  const handleClear = (e) => { e.stopPropagation(); onChange(null, null); setSearch(''); };
  const handleCreate = async () => {
    if (!onCreate || !search.trim() || creating) return;
    setCreating(true);
    try {
      const newOpt = await onCreate(search.trim());
      onChange(newOpt.id, newOpt.label);
      setSearch(''); setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur création');
    } finally { setCreating(false); }
  };

  const displayValue = open ? search : (valueLabel || '');

  return (
    <div className="relative">
      <div className="relative">
        <input
          data-testid={testId}
          type="text"
          className="w-full pl-3 pr-8 py-2.5 rounded-lg border text-sm"
          style={{ borderColor: 'var(--text-disabled)' }}
          value={displayValue}
          onChange={e => setSearch(e.target.value)}
          onFocus={() => { setOpen(true); setSearch(''); }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={placeholder}
          disabled={disabled}
        />
        {valueLabel && !open ? (
          <button type="button" onMouseDown={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-100 rounded">
            <X size={13} style={{ color: 'var(--text-muted)' }} />
          </button>
        ) : (
          <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--text-placeholder)' }} />
        )}
      </div>
      {open && (
        <div className="absolute z-30 w-full mt-1 bg-white rounded-lg shadow-lg border overflow-hidden max-h-56 overflow-y-auto"
          style={{ borderColor: 'var(--border)' }}>
          {filtered.length === 0 && !search && (
            <div className="px-3 py-4 text-center">
              <p className="text-xs" style={{ color: 'var(--text-placeholder)' }}>Aucune option — tapez pour créer</p>
            </div>
          )}
          {filtered.map(opt => (
            <button key={opt.id} type="button" onMouseDown={() => handleSelect(opt)}
              className="w-full px-3 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-2 border-b last:border-0"
              style={{ borderColor: 'var(--border-light)', color: opt.id === value ? 'var(--primary-solid)' : 'var(--text)' }}>
              {opt.id === value ? <Check size={13} style={{ color: 'var(--primary-solid)', flexShrink: 0 }} /> : <span className="w-[13px]" />}
              {opt.label}
            </button>
          ))}
          {search.trim() && !exactMatch && onCreate && (
            <button type="button" onMouseDown={handleCreate} disabled={creating}
              className="w-full px-3 py-2.5 text-left text-sm hover:bg-green-50 flex items-center gap-2 border-t"
              style={{ borderColor: 'var(--border)', color: 'var(--primary-solid)' }}>
              <Plus size={13} style={{ flexShrink: 0 }} />
              {creating ? 'Création...' : `Créer "${search.trim()}"`}
            </button>
          )}
          {filtered.length === 0 && search.trim() && !onCreate && (
            <div className="px-3 py-3 text-center">
              <p className="text-xs" style={{ color: 'var(--text-placeholder)' }}>Aucun résultat</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ─── Compteur Tab (v2 : multi-compteurs par matériel) ────────────────────────
const COMPTEUR_TYPES = {
  HEURES_MOTEUR: { label: 'Compteur horaire',      unit: 'h' },
  KILOMETRES:    { label: 'Compteur kilométrique', unit: 'km' },
  CYCLES:        { label: 'Compteur de cycles',    unit: 'cycles' },
};

function CompteurCard({ compteur, onRefresh }) {
  const [releves, setReleves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingReleve, setEditingReleve] = useState(null);
  const [formVal, setFormVal] = useState({ valeur: '', date: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);

  const meta = COMPTEUR_TYPES[compteur.type_compteur] || { label: compteur.type_compteur, unit: compteur.unite_mesure };
  const unit = compteur.unite_mesure || meta.unit;

  const loadReleves = useCallback(async () => {
    try {
      const res = await api.getRelevesV2(compteur.compteur_id);
      setReleves(res.data);
    } catch { toast.error('Erreur chargement relevés'); }
    finally { setLoading(false); }
  }, [compteur.compteur_id]);

  useEffect(() => { loadReleves(); }, [loadReleves]);

  const sorted = [...releves].sort((a, b) => a.date_releve.localeCompare(b.date_releve));
  const calcAvg = () => {
    if (sorted.length < 2) return null;
    const days = (new Date(sorted[sorted.length - 1].date_releve) - new Date(sorted[0].date_releve)) / 86400000;
    if (days <= 0) return null;
    return ((sorted[sorted.length - 1].valeur_saisie - sorted[0].valeur_saisie) / days).toFixed(1);
  };
  const avg = calcAvg();

  const openAdd = () => {
    setEditingReleve(null);
    setFormVal({
      valeur: compteur.valeur_actuelle != null ? String(compteur.valeur_actuelle) : '',
      date: new Date().toISOString().slice(0, 10),
    });
    setShowForm(true);
  };
  const openEdit = (r) => {
    setEditingReleve(r);
    setFormVal({ valeur: String(r.valeur_saisie), date: r.date_releve });
    setShowForm(true);
  };
  const cancel = () => { setShowForm(false); setEditingReleve(null); };

  const handleSave = async () => {
    const val = parseFloat(formVal.valeur);
    if (isNaN(val) || !formVal.date) { toast.error('Valeur et date obligatoires'); return; }
    setSaving(true);
    try {
      if (editingReleve) {
        await api.updateReleveV2(editingReleve.releve_id, { valeur: val, date: formVal.date });
        toast.success('Relevé mis à jour');
      } else {
        await api.createReleveV2(compteur.compteur_id, { valeur: val, date: formVal.date });
        toast.success('Relevé ajouté');
      }
      setShowForm(false); setEditingReleve(null);
      await loadReleves();
      await onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const handleDelete = async (r) => {
    try {
      await api.deleteReleveV2(r.releve_id);
      toast.success('Relevé supprimé');
      await loadReleves();
      await onRefresh();
    } catch { toast.error('Erreur suppression'); }
  };

  const handleSetPrincipal = async () => {
    try {
      await api.updateCompteurV2(compteur.compteur_id, { est_principal: true });
      toast.success('Compteur défini comme principal');
      await onRefresh();
    } catch { toast.error('Erreur'); }
  };

  const handleToggleActif = async () => {
    try {
      await api.updateCompteurV2(compteur.compteur_id, { est_actif: !compteur.est_actif });
      toast.success(compteur.est_actif ? 'Compteur désactivé (remplacé)' : 'Compteur réactivé');
      await onRefresh();
    } catch { toast.error('Erreur'); }
  };

  const inp = "px-3 py-2 rounded-lg border text-sm";
  const inpStyle = { borderColor: 'var(--text-disabled)' };

  return (
    <div className="rounded-xl border overflow-hidden"
      data-testid={`compteur-card-${compteur.compteur_id}`}
      style={{ borderColor: 'var(--border)', opacity: compteur.est_actif ? 1 : 0.65 }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5" style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid #E6E6E1' }}>
        <div className="flex items-center gap-2 flex-wrap">
          <Gauge size={14} style={{ color: 'var(--primary-solid)' }} />
          <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{meta.label}</span>
          {compteur.est_principal && (
            <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold"
              style={{ background: '#FEF3C7', color: '#B45309' }}>
              <Star size={10} /> Principal
            </span>
          )}
          {!compteur.est_actif && (
            <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
              style={{ background: '#F3F4F6', color: '#6B7280' }}>
              Remplacé
            </span>
          )}
          {compteur.valeur_actuelle != null && (
            <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
              {compteur.valeur_actuelle.toLocaleString('fr-FR')} {unit}
            </span>
          )}
          {avg && compteur.est_actif && (
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
              moy. {avg} {unit}/j
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {compteur.est_actif && (
            <button type="button" onClick={openAdd}
              data-testid={`compteur-add-releve-${compteur.compteur_id}`}
              className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg text-white"
              style={{ background: 'var(--primary-gradient)' }}>
              <Plus size={12} /> Nouvelle saisie
            </button>
          )}
          {compteur.est_actif && !compteur.est_principal && (
            <button type="button" onClick={handleSetPrincipal} title="Définir comme compteur principal (alimente la fiche matériel)"
              data-testid={`compteur-set-principal-${compteur.compteur_id}`}
              className="p-1.5 rounded-lg border" style={{ borderColor: 'var(--border)', color: '#B45309' }}>
              <Star size={13} />
            </button>
          )}
          {compteur.est_actif ? (
            <button type="button" onClick={handleToggleActif} title="Désactiver (compteur remplacé) — l'historique est conservé"
              data-testid={`compteur-disable-${compteur.compteur_id}`}
              className="p-1.5 rounded-lg border" style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
              <Archive size={13} />
            </button>
          ) : (
            <button type="button" onClick={handleToggleActif} title="Réactiver ce compteur"
              data-testid={`compteur-enable-${compteur.compteur_id}`}
              className="p-1.5 rounded-lg border" style={{ borderColor: 'var(--border)', color: 'var(--primary-solid)' }}>
              <CheckCircle size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Inline form saisie */}
      {showForm && (
        <div className="px-4 py-3 border-b flex items-end gap-3" style={{ borderColor: 'var(--border)', background: '#FAFFF8' }}>
          <div>
            <label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Date</label>
            <input type="date" className={inp} style={inpStyle}
              value={formVal.date} onChange={e => setFormVal(f => ({ ...f, date: e.target.value }))} />
          </div>
          <div>
            <label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Valeur ({unit})</label>
            <input type="number" className={`${inp} w-36`} style={inpStyle} min="0" step="0.1"
              value={formVal.valeur} onChange={e => setFormVal(f => ({ ...f, valeur: e.target.value }))}
              placeholder="ex: 1500" />
          </div>
          <button type="button" onClick={handleSave} disabled={saving}
            data-testid="releve-save-btn"
            className="px-4 py-2 rounded-lg text-sm font-medium text-white"
            style={{ background: saving ? 'var(--primary-light)' : 'var(--primary-gradient)' }}>
            {saving ? '...' : (editingReleve ? 'Mettre à jour' : 'Ajouter')}
          </button>
          <button type="button" onClick={cancel}
            className="px-3 py-2 rounded-lg text-sm" style={{ color: 'var(--text-muted)' }}>Annuler</button>
        </div>
      )}

      {/* Relevés */}
      {loading ? (
        <div className="flex justify-center py-6">
          <div className="animate-spin w-5 h-5 border-2 border-t-transparent rounded-full"
            style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
        </div>
      ) : sorted.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm" style={{ color: 'var(--text-placeholder)' }}>
          Aucune saisie{compteur.est_actif ? ' — cliquez sur "+ Nouvelle saisie"' : ''}
        </div>
      ) : (
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr style={{ borderBottom: '1px solid #F0F0EC', background: '#FAFAFA' }}>
              <th className="px-4 py-2 text-left text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Date</th>
              <th className="px-4 py-2 text-right text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Valeur</th>
              <th className="px-4 py-2 text-right text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Variation</th>
              <th className="px-4 py-2 text-right text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Moy./jour</th>
              <th className="px-4 py-2 text-left text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Source</th>
              <th className="px-4 py-2 w-16"></th>
            </tr>
          </thead>
          <tbody>
            {[...sorted].reverse().map((r, revIdx) => {
              const realIdx = sorted.length - 1 - revIdx;
              const prev = sorted[realIdx - 1];
              const diff = prev != null ? r.valeur_saisie - prev.valeur_saisie : null;
              const days = prev ? (new Date(r.date_releve) - new Date(prev.date_releve)) / 86400000 : null;
              const dailyAvg = diff != null && days > 0 ? (diff / days).toFixed(1) : null;
              const isEditing = editingReleve?.releve_id === r.releve_id;
              return (
                <tr key={r.releve_id} className="border-b group"
                  style={{ borderColor: 'var(--border-light)', background: isEditing ? 'var(--primary-50)' : undefined }}>
                  <td className="px-4 py-2.5" style={{ color: 'var(--text-secondary)' }}>
                    {new Date(r.date_releve + 'T12:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                  <td className="px-4 py-2.5 text-right font-medium" style={{ color: 'var(--text)' }}>
                    {r.valeur_saisie.toLocaleString('fr-FR')} {unit}
                  </td>
                  <td className="px-4 py-2.5 text-right text-xs" style={{ color: diff != null && diff >= 0 ? '#22C55E' : 'var(--text-muted)' }}>
                    {diff != null ? `+${diff.toLocaleString('fr-FR')} ${unit}` : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right text-xs" style={{ color: 'var(--text-muted)' }}>
                    {dailyAvg ? `${dailyAvg} ${unit}/j` : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-xs" style={{ color: 'var(--text-muted)' }}>
                    {r.source === 'Ordre réparation' && r.ordre_numero ? r.ordre_numero : (r.source || '—')}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity justify-end">
                      <button type="button" onClick={() => openEdit(r)}
                        className="p-1 hover:bg-blue-50 rounded"><Edit size={12} style={{ color: 'var(--primary-light)' }} /></button>
                      <button type="button" onClick={() => handleDelete(r)}
                        className="p-1 hover:bg-red-50 rounded"><Trash2 size={12} style={{ color: '#DC2626' }} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CompteurTab({ materiel, form, setForm }) {
  const [compteurs, setCompteurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    type_compteur: 'HEURES_MOTEUR', valeur_initiale: '', date_initiale: new Date().toISOString().slice(0, 10),
  });
  const [creating, setCreating] = useState(false);

  const loadCompteurs = useCallback(async () => {
    if (!materiel?.materiel_id) { setLoading(false); return; }
    try {
      const res = await api.getCompteursV2(materiel.materiel_id);
      setCompteurs(res.data);
    } catch { toast.error('Erreur chargement compteurs'); }
    finally { setLoading(false); }
  }, [materiel?.materiel_id]);

  useEffect(() => { loadCompteurs(); }, [loadCompteurs]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const payload = { type_compteur: createForm.type_compteur };
      if (createForm.valeur_initiale !== '') {
        payload.valeur_initiale = parseFloat(createForm.valeur_initiale);
        payload.date_initiale = createForm.date_initiale;
      }
      await api.createCompteurV2(materiel.materiel_id, payload);
      toast.success('Compteur ajouté');
      setShowCreate(false);
      setCreateForm({ type_compteur: 'HEURES_MOTEUR', valeur_initiale: '', date_initiale: new Date().toISOString().slice(0, 10) });
      await loadCompteurs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur création');
    } finally { setCreating(false); }
  };

  const hasH = form.has_compteur_h;
  const hasKm = form.has_compteur_km;

  if (!materiel?.materiel_id) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <Clock size={32} className="mb-3" style={{ color: 'var(--text-disabled)' }} />
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Enregistrez d'abord le matériel pour gérer ses compteurs.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Type toggles (flags utilisés par atelier / BL / entretien) */}
      <div className="flex items-center gap-6 p-4 rounded-xl border" style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
        <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Types de compteurs :</span>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={hasKm} onChange={e => setForm(f => ({ ...f, has_compteur_km: e.target.checked }))}
            className="w-4 h-4 rounded" style={{ accentColor: 'var(--primary-solid)' }} />
          <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Kilométrique (km)</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={hasH} onChange={e => setForm(f => ({ ...f, has_compteur_h: e.target.checked }))}
            className="w-4 h-4 rounded" style={{ accentColor: 'var(--primary-solid)' }} />
          <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Horaire (h)</span>
        </label>
        <button type="button" onClick={() => setShowCreate(s => !s)}
          data-testid="compteur-create-toggle"
          className="ml-auto flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg border"
          style={{ borderColor: 'var(--border)', color: 'var(--primary-solid)' }}>
          <Plus size={12} /> Ajouter un compteur
        </button>
      </div>

      {/* Formulaire création compteur */}
      {showCreate && (
        <div className="p-4 rounded-xl border flex items-end gap-3 flex-wrap"
          data-testid="compteur-create-form"
          style={{ borderColor: 'var(--border)', background: '#FAFFF8' }}>
          <div>
            <label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Type de compteur</label>
            <select className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--text-disabled)' }}
              value={createForm.type_compteur}
              onChange={e => setCreateForm(f => ({ ...f, type_compteur: e.target.value }))}>
              {Object.entries(COMPTEUR_TYPES).map(([k, v]) => (
                <option key={k} value={k}>{v.label} ({v.unit})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Valeur initiale (optionnel)</label>
            <input type="number" className="px-3 py-2 rounded-lg border text-sm w-36" style={{ borderColor: 'var(--text-disabled)' }}
              min="0" step="0.1" placeholder="ex: 0"
              value={createForm.valeur_initiale}
              onChange={e => setCreateForm(f => ({ ...f, valeur_initiale: e.target.value }))} />
          </div>
          <div>
            <label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Date du relevé initial</label>
            <input type="date" className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--text-disabled)' }}
              value={createForm.date_initiale}
              onChange={e => setCreateForm(f => ({ ...f, date_initiale: e.target.value }))} />
          </div>
          <button type="button" onClick={handleCreate} disabled={creating}
            data-testid="compteur-create-submit"
            className="px-4 py-2 rounded-lg text-sm font-medium text-white"
            style={{ background: creating ? 'var(--primary-light)' : 'var(--primary-gradient)' }}>
            {creating ? '...' : 'Créer le compteur'}
          </button>
          <button type="button" onClick={() => setShowCreate(false)}
            className="px-3 py-2 rounded-lg text-sm" style={{ color: 'var(--text-muted)' }}>Annuler</button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-6">
          <div className="animate-spin w-6 h-6 border-2 border-t-transparent rounded-full"
            style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
        </div>
      ) : compteurs.length === 0 ? (
        <p className="text-sm text-center py-6" style={{ color: 'var(--text-placeholder)' }}>
          Aucun compteur — cliquez sur "+ Ajouter un compteur".
        </p>
      ) : (
        compteurs.map(c => (
          <CompteurCard key={c.compteur_id} compteur={c} onRefresh={loadCompteurs} />
        ))
      )}
    </div>
  );
}


// ─── PlanEntretienBlock ───────────────────────────────────────────────────────
const SCOPE_PORTEE = {
  serie:   { label: 'N° de série', color: '#B45309' },
  article: { label: 'Article',     color: '#7C3AED' },
  modele:  { label: 'Modèle',      color: '#1D4ED8' },
};
const STATUT_PRV_LABEL = {
  a_faire:              { label: 'À faire',       color: '#7C3AED' },
  notifie_par_mail:     { label: 'Notifié',        color: '#065F46' },
  en_attente:           { label: 'En attente',     color: '#1D4ED8' },
  aucun_compteur_saisi: { label: 'Sans compteur',  color: '#B91C1C' },
  reporte:              { label: 'Reporté',        color: '#6B7280' },
};

function PlanEntretienBlock({ materiel, resolvedPlan, loadingPlan }) {
  const [preventifs, setPreventifs] = React.useState([]);
  const [loadingPrv, setLoadingPrv] = React.useState(false);

  React.useEffect(() => {
    if (!materiel?.materiel_id) return;
    setLoadingPrv(true);
    api.getPreventifs({ materiel_id: materiel.materiel_id, limit: 10 })
      .then(r => {
        const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        // Filtrer les alertes actives (hors converti_en_or et annule)
        setPreventifs(items.filter(p => !['converti_en_or', 'annule'].includes(p.statut)));
      })
      .catch(() => setPreventifs([]))
      .finally(() => setLoadingPrv(false));
  }, [materiel?.materiel_id]);

  if (loadingPlan) return (
    <div className="flex justify-center py-3">
      <div className="animate-spin w-4 h-4 rounded-full border-2" style={{ borderColor: 'var(--primary-100)', borderTopColor: 'var(--primary-solid)' }} />
    </div>
  );

  if (!resolvedPlan?.plan) return (
    <div className="p-3 rounded-xl border text-xs" style={{ borderColor: 'var(--border)', background: '#FFFBEB', color: '#B45309' }}>
      Aucun plan applicable — créez-en un dans <strong>Atelier → Plans d'entretien</strong>.
    </div>
  );

  const p = resolvedPlan.plan;
  const portee = SCOPE_PORTEE[resolvedPlan.origine_plan] || SCOPE_PORTEE.modele;
  const typeLabel = { h: 'Heures', km: 'Km', cycles: 'Cycles', les_deux: 'H+Km' }[p.type_compteur] || p.type_compteur;

  return (
    <div className="space-y-2">
      {/* Ligne plan : nom · portée · préavis */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{p.nom}</span>
        <span className="text-xs px-1.5 py-0.5 rounded font-semibold"
          style={{ background: portee.color + '18', color: portee.color }}>
          {portee.label}
        </span>
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {p.preavis_jours ?? 30} j préavis · {typeLabel}
        </span>
      </div>

      {/* Prochaine(s) étape(s) visée(s) */}
      {loadingPrv ? (
        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Chargement…</div>
      ) : preventifs.length === 0 ? (
        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Aucune alerte active — la prochaine sera calculée lors de la sync.</div>
      ) : preventifs.map(prv => {
        const sc = STATUT_PRV_LABEL[prv.statut] || { label: prv.statut, color: '#6B7280' };
        return (
          <div key={prv.preventif_id} className="flex items-center gap-2 flex-wrap">
            <span className="text-xs px-1.5 py-0.5 rounded font-semibold"
              style={{ background: sc.color + '15', color: sc.color }}>
              {sc.label}
            </span>
            {prv.etape_nom && <span className="text-xs font-medium" style={{ color: 'var(--text)' }}>{prv.etape_nom}</span>}
            {prv.compteur_cible && (
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                → {Number(prv.compteur_cible).toLocaleString('fr-FR')} {typeLabel}
              </span>
            )}
            {prv.date_prevue_seuil && (
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                · {new Date(prv.date_prevue_seuil).toLocaleDateString('fr-FR')}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── PreventifTab ─────────────────────────────────────────────────────────────
function PreventifTab({ materiel, form, setForm }) {
  const [resolvedPlan, setResolvedPlan] = useState(null);
  const [loadingPlan, setLoadingPlan]   = useState(false);

  useEffect(() => {
    if (!materiel?.materiel_id) return;
    setLoadingPlan(true);
    api.resolveGammeMateriel(materiel.materiel_id)
      .then(r => setResolvedPlan(r.data))
      .catch(() => setResolvedPlan(null))
      .finally(() => setLoadingPlan(false));
  }, [materiel?.materiel_id]);

  const [modeDropOpen, setModeDropOpen] = useState(false);
  const modeDropRef = useRef(null);

  // Fermer le dropdown au clic extérieur
  React.useEffect(() => {
    if (!modeDropOpen) return;
    const handler = (e) => { if (modeDropRef.current && !modeDropRef.current.contains(e.target)) setModeDropOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [modeDropOpen]);

  const currentMode = form.refus_suivi_preventif ? 'refus'
    : form.prospection_commerciale ? 'prospection'
    : form.suivi_preventif_actif ? 'activer'
    : null;

  const handleModeChange = (mode) => {
    if (mode === 'activer') {
      setForm(f => ({ ...f, suivi_preventif_actif: true, prospection_commerciale: false, refus_suivi_preventif: false }));
    } else if (mode === 'prospection') {
      setForm(f => ({ ...f, suivi_preventif_actif: false, prospection_commerciale: true, refus_suivi_preventif: false }));
    } else {
      setForm(f => ({ ...f, suivi_preventif_actif: false, prospection_commerciale: false, refus_suivi_preventif: true }));
    }
    setModeDropOpen(false);
  };

  const MODES = [
    { key: 'activer',     label: 'Suivi préventif actif',   dot: '#10B981', desc: 'Alertes dans le cockpit Atelier' },
    { key: 'prospection', label: 'Prospection commerciale', dot: '#3B82F6', desc: 'Relance email uniquement' },
    { key: 'refus',       label: 'Refus de suivi',          dot: '#DC2626', desc: 'Ignoré par le batch' },
  ];

  const currentModeCfg = MODES.find(m => m.key === currentMode);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-bold mb-3 uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
          Paramètres du suivi préventif
        </h3>
        <div ref={modeDropRef} className="relative inline-flex items-center gap-2">
          {/* Badge état actuel */}
          {currentModeCfg ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold"
              style={{ background: `${currentModeCfg.dot}15`, color: currentModeCfg.dot, border: `1px solid ${currentModeCfg.dot}44` }}>
              <span className="w-2 h-2 rounded-full" style={{ background: currentModeCfg.dot }} />
              {currentModeCfg.label}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold"
              style={{ background: '#F9FAFB', color: '#6B7280', border: '1px solid #E5E7EB' }}>
              <span className="w-2 h-2 rounded-full" style={{ background: '#D1D5DB' }} />
              Sélectionnez le suivi
            </span>
          )}
          {/* Bouton Modifier */}
          <button type="button" onClick={() => setModeDropOpen(o => !o)}
            data-testid="preventif-mode-modifier"
            className="text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition-colors hover:bg-gray-50"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
            Modifier
          </button>
          {/* Dropdown stylé */}
          {modeDropOpen && (
            <div className="absolute top-full left-0 mt-1 z-50 rounded-xl border shadow-xl py-1.5 min-w-[260px]"
              style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
              {MODES.map(m => {
                const active = currentMode === m.key;
                return (
                  <button key={m.key} type="button"
                    data-testid={`preventif-mode-${m.key}`}
                    onClick={() => handleModeChange(m.key)}
                    className="w-full text-left flex items-start gap-2.5 px-4 py-2.5 transition-colors"
                    style={{ background: active ? `${m.dot}10` : 'transparent' }}>
                    <span className="w-2 h-2 rounded-full mt-1 shrink-0" style={{ background: m.dot }} />
                    <div>
                      <p className="text-sm font-semibold" style={{ color: active ? m.dot : 'var(--text)' }}>{m.label}</p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{m.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Plan d'entretien + prochaine étape */}
      {currentMode === 'activer' && (
        <PlanEntretienBlock
          materiel={materiel}
          resolvedPlan={resolvedPlan}
          loadingPlan={loadingPlan}
        />
      )}
    </div>
  );
}



// ─── AnnuaireSearchField ──────────────────────────────────────────────────────
// Dropdown cherchable dans l'annuaire (entreprises + personnes)
// Affiche un chip cliquable si lié, un chip grisé si nom connu mais non lié
function AnnuaireSearchField({ value, onChange }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    if (search.length < 1) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const [eRes, pRes] = await Promise.all([
          api.getEntreprises({ search, slim: true }),
          api.getPersonnes({ search }),
        ]);
        const ents = (eRes.data || []).filter(e => e.nom).slice(0, 8).map(e => ({
          id: e.entreprise_id, nom: e.nom, type: 'entreprise', ville: e.ville,
        }));
        const pers = (pRes.data || []).filter(p => p.nom || p.prenom).slice(0, 5).map(p => ({
          id: p.personne_id,
          nom: [p.prenom, p.nom].filter(Boolean).join(' '),
          type: 'personne',
        }));
        setResults([...ents, ...pers].slice(0, 10));
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const select = (item) => {
    onChange({ id: item.id, nom: item.nom, type: item.type });
    setSearch(''); setResults([]); setOpen(false);
  };
  const clear = () => { onChange(null); setSearch(''); };

  const inp = "w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none";
  const inpStyle = { borderColor: 'var(--text-disabled)' };

  // Chip quand sélectionné (lié à l'annuaire)
  if (value && value.id) {
    const isEnt = !value.type || value.type === 'entreprise';
    const link = isEnt ? `/annuaire?entreprise=${value.id}` : `/annuaire?personne=${value.id}`;
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg border"
        style={{ background: 'var(--primary-50)', borderColor: 'var(--primary-100)' }}>
        {isEnt ? <Building2 size={14} style={{ color: 'var(--primary-solid)', flexShrink: 0 }} />
                : <User size={14} style={{ color: 'var(--primary-solid)', flexShrink: 0 }} />}
        <button type="button" onClick={() => window.open(link, '_blank')}
          className="flex-1 text-left text-sm font-medium truncate hover:underline"
          style={{ color: 'var(--primary-solid)' }} title="Ouvrir la fiche annuaire">
          {value.nom}
        </button>
        <ExternalLink size={12} style={{ color: 'var(--primary-light)', flexShrink: 0 }} />
        <button type="button" onClick={clear}
          className="p-0.5 rounded hover:bg-red-50 flex-shrink-0" style={{ color: '#DC2626' }}>
          <X size={12} />
        </button>
      </div>
    );
  }

  // Chip grisé quand nom connu mais pas lié à l'annuaire (ex: import Odoo)
  if (value && value.nom) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg border"
        style={{ borderColor: 'var(--border)', background: 'var(--bg-secondary)' }}>
        <Building2 size={14} style={{ color: 'var(--text-placeholder)', flexShrink: 0 }} />
        <span className="flex-1 text-sm truncate" style={{ color: 'var(--text-muted)' }}>{value.nom}</span>
        <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: '#FEF9C3', color: '#92400E', fontSize: 10 }}>
          non lié
        </span>
        <button type="button" onClick={clear}
          className="p-0.5 rounded hover:bg-red-50 flex-shrink-0" style={{ color: '#DC2626' }}>
          <X size={12} />
        </button>
      </div>
    );
  }

  // Champ de recherche
  return (
    <div className="relative" ref={ref}>
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
        style={{ color: 'var(--text-placeholder)' }} />
      <input type="text" value={search}
        onChange={e => { setSearch(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Rechercher dans l'annuaire..."
        className={`${inp} pl-9 pr-3`} style={inpStyle} />
      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-lg border shadow-lg overflow-hidden"
          style={{ borderColor: 'var(--border)' }}>
          {results.map(item => (
            <button key={item.id} type="button" onMouseDown={() => select(item)}
              className="w-full text-left px-3 py-2.5 hover:bg-gray-50 flex items-center gap-3 border-b last:border-0"
              style={{ borderColor: 'var(--border-light)' }}>
              {item.type === 'personne'
                ? <User size={13} style={{ color: 'var(--text-placeholder)', flexShrink: 0 }} />
                : <Building2 size={13} style={{ color: 'var(--text-placeholder)', flexShrink: 0 }} />}
              <span className="text-sm flex-1 truncate" style={{ color: 'var(--text)' }}>{item.nom}</span>
              <span className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 capitalize"
                style={{ background: 'var(--bg-subtle)', color: 'var(--text-muted)' }}>
                {item.type === 'personne' ? 'personne' : item.ville || 'entreprise'}
              </span>
            </button>
          ))}
        </div>
      )}
      {open && search.length >= 1 && results.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-lg border shadow-lg p-3 text-center"
          style={{ borderColor: 'var(--border)' }}>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Aucun résultat</p>
        </div>
      )}
    </div>
  );
}


// Alias pour la compatibilité
const EntrepriseSearchField = AnnuaireSearchField;

// ─── ArticleSearchDropdown ───────────────────────────────────────────────────

function ArticleSearchDropdown({ value, options, onChange }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const selected = options.find(a => a.article_id === value);
  const filtered = options.filter(a => !search.trim() ||
    (a.reference || '').toLowerCase().includes(search.toLowerCase()) ||
    (a.designation || '').toLowerCase().includes(search.toLowerCase())
  );

  if (selected) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg border"
        style={{ background: 'var(--primary-50)', borderColor: '#BFDBFE' }}>
        <button type="button" onClick={() => navigate(`/articles?open=${selected.article_id}`)}
          className="flex-1 min-w-0 text-left hover:underline transition-none">
          <p className="text-sm font-medium truncate" style={{ color: 'var(--primary-solid)' }}>{selected.designation}</p>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{selected.reference}</p>
        </button>
        <button type="button" onClick={() => onChange(null)}
          className="p-1 hover:bg-red-50 rounded flex-shrink-0" style={{ color: '#DC2626' }}>
          <X size={12} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
          style={{ color: 'var(--text-placeholder)' }} />
        <input value={search} onChange={e => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Rechercher par référence ou désignation..."
          data-testid="materiel-article-search"
          className="w-full pl-8 pr-3 py-2 border rounded-lg text-sm focus:outline-none"
          style={{ borderColor: 'var(--border)' }} />
      </div>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-xl shadow-xl overflow-hidden"
          style={{ borderColor: 'var(--border)' }}>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0
              ? <p className="px-3 py-3 text-xs text-center" style={{ color: 'var(--text-placeholder)' }}>Aucun article matériel trouvé</p>
              : filtered.map(a => (
                <button key={a.article_id} type="button"
                  onMouseDown={() => { onChange(a); setSearch(''); setOpen(false); }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2.5"
                  style={{ borderBottom: '1px solid #F2F2EE' }}>
                  <span className="font-mono text-xs px-1.5 py-0.5 rounded flex-shrink-0"
                    style={{ background: 'var(--border-light)', color: '#555' }}>{a.reference}</span>
                  <span className="text-sm truncate" style={{ color: 'var(--text)' }}>{a.designation}</span>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── FinancementTab ──────────────────────────────────────────────────────────

const FIN_TYPE_COLORS = {
  Leasing: { bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
  SNC:     { bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0' },
};

function FinancementTab({ materiel, form, setForm }) {
  const [financements, setFinancements] = useState([]);
  const [loadingFin, setLoadingFin] = useState(true);

  useEffect(() => {
    if (!materiel?.materiel_id) { setLoadingFin(false); return; }
    setLoadingFin(true);
    api.getFinancements({ materiel_id: materiel.materiel_id })
      .then(res => setFinancements(Array.isArray(res.data) ? res.data : []))
      .catch(() => toast.error('Erreur chargement financement'))
      .finally(() => setLoadingFin(false));
  }, [materiel?.materiel_id]);

  const fmtNum = (n, d = 0) => n != null ? Number(n).toLocaleString('fr-FR', { minimumFractionDigits: d, maximumFractionDigits: d }).replace(/[\u00A0\u202F ]/g, '.') : '—';
  const fmtDate = (s) => s ? new Date(s + 'T12:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  if (!materiel?.materiel_id) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <CreditCard size={32} className="mb-3" style={{ color: 'var(--text-disabled)' }} />
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Enregistrez d'abord le matériel pour voir son financement.</p>
      </div>
    );
  }

  if (loadingFin) {
    return (
      <div className="flex justify-center py-10">
        <div className="animate-spin w-6 h-6 border-2 border-t-transparent rounded-full"
          style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (financements.length > 0) {
    const today = new Date().toISOString().split('T')[0];
    const fin = financements[0]; // un matériel n'a qu'un seul financement
    const isExpired = fin.date_fin && fin.date_fin < today;

    return (
      <div className="space-y-4">
        {financements.map(f => {
          const c = FIN_TYPE_COLORS[f.type] || { bg: '#F3F4F6', color: '#6B7280', border: '#E5E7EB' };
          return (
            <div key={f.financement_id} className="rounded-xl border overflow-hidden"
              style={{ borderColor: c.border }}>
              {/* En-tête */}
              <div className="flex items-center justify-between px-4 py-3"
                style={{ background: c.bg, borderBottom: `1px solid ${c.border}` }}>
                <div className="flex items-center gap-3">
                  <CreditCard size={16} style={{ color: c.color }} />
                  <div>
                    <p className="text-sm font-bold" style={{ color: c.color,  }}>
                      Contrat {f.numero_contrat}
                    </p>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                      style={{ background: 'white', color: c.color, border: `1px solid ${c.border}` }}>
                      {f.type}
                    </span>
                  </div>
                </div>
                <button type="button"
                  onClick={() => window.open('/financements', '_blank')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                  style={{ background: 'white', color: c.color, border: `1px solid ${c.border}` }}>
                  Voir <ArrowUpRight size={12} />
                </button>
              </div>
              {/* Détails */}
              <div className="px-4 py-3 grid grid-cols-2 gap-3" style={{ background: 'white' }}>
                {f.entreprise_nom && (
                  <div className="col-span-2">
                    <p className="text-xs font-semibold uppercase tracking-wide mb-0.5"
                      style={{ color: 'var(--text-muted)' }}>Organisme financier</p>
                    <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{f.entreprise_nom}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide mb-0.5"
                    style={{ color: 'var(--text-muted)' }}>Capital emprunté</p>
                  <p className="text-base font-bold" style={{ color: 'var(--primary-solid)',  }}>
                    {fmtNum(f.capital_emprunte)} €
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide mb-0.5"
                    style={{ color: 'var(--text-muted)' }}>Mensualité</p>
                  <p className="text-base font-bold" style={{ color: '#B45309',  }}>
                    {fmtNum(f.mensualite)} € <span className="text-xs font-normal">/mois</span>
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide mb-0.5"
                    style={{ color: 'var(--text-muted)' }}>Durée</p>
                  <p className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>
                    {f.duree ? `${f.duree} mois` : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide mb-0.5"
                    style={{ color: 'var(--text-muted)' }}>Taux annuel</p>
                  <p className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>
                    {f.taux ? `${Number(f.taux).toFixed(2)} %` : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide mb-0.5"
                    style={{ color: 'var(--text-muted)' }}>Début</p>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{fmtDate(f.date_debut)}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide mb-0.5"
                    style={{ color: 'var(--text-muted)' }}>Fin</p>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{fmtDate(f.date_fin)}</p>
                </div>
              </div>
            </div>
          );
        })}

        {/* Champ immobilisation — visible si le financement est terminé (Leasing ou SNC) */}
        {isExpired && (
          <div className="space-y-3 pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5"
                style={{ color: 'var(--text-muted)',  }}>
                Numéro d'immobilisation
              </label>
              <input
                type="text"
                value={form.numero_immobilisation || ''}
                onChange={e => setForm(f => ({ ...f, numero_immobilisation: e.target.value }))}
                placeholder="Ex : IMM-2025-0042"
                data-testid="form-numero-immobilisation"
                className="w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none"
                style={{ borderColor: 'var(--border)' }}
              />
              <p className="text-xs mt-1" style={{ color: 'var(--text-placeholder)' }}>
                Contrat {fin.type} terminé le {fmtDate(fin.date_fin)} — ce matériel peut être comptabilisé en immobilisation. Sauvegardez pour conserver ce numéro.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Aucun financement → matériel immobilisé
  return (
    <div className="space-y-4">
      {/* Info immobilisation */}
      <div className="rounded-xl border p-4" style={{ background: '#F8FAFC', borderColor: '#CBD5E1' }}>
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: '#E0E7FF' }}>
            <Hash size={16} style={{ color: '#4338CA' }} />
          </div>
          <div>
            <p className="text-sm font-semibold" style={{ color: '#1E293B',  }}>
              Matériel immobilisé
            </p>
            <p className="text-xs mt-0.5 leading-relaxed" style={{ color: '#64748B' }}>
              Ce matériel n'est rattaché à aucun contrat de financement. Il est comptabilisé
              en immobilisation dans le patrimoine de la société.
            </p>
          </div>
        </div>
      </div>

      {/* Numéro d'immobilisation */}
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5"
          style={{ color: 'var(--text-muted)',  }}>
          Numéro d'immobilisation
        </label>
        <input
          type="text"
          value={form.numero_immobilisation || ''}
          onChange={e => setForm(f => ({ ...f, numero_immobilisation: e.target.value }))}
          placeholder="Ex : IMM-2025-0042"
          data-testid="form-numero-immobilisation"
          className="w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none"
          style={{ borderColor: 'var(--border)' }}
        />
        <p className="text-xs mt-1" style={{ color: 'var(--text-placeholder)' }}>
          Référence comptable de l'immobilisation dans le patrimoine de la société. Sauvegardez le formulaire pour conserver ce numéro.
        </p>
      </div>

      {/* Lien vers financement */}
      <button type="button"
        onClick={() => window.open('/financements', '_blank')}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-colors hover:bg-blue-50"
        style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
        <CreditCard size={15} /> Créer un contrat de financement
        <ArrowUpRight size={13} />
      </button>
    </div>
  );
}


// ─── PiecesJointesTab ─────────────────────────────────────────────────────────

function PiecesJointesTab({ materiel }) {
  const [pjs, setPjs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingNom, setEditingNom] = useState('');
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    if (!materiel?.materiel_id) return;
    try {
      const r = await api.getPiecesJointes(materiel.materiel_id);
      setPjs(r.data || []);
    } catch {
      toast.error('Impossible de charger les pièces jointes');
    } finally {
      setLoading(false);
    }
  }, [materiel?.materiel_id]);

  useEffect(() => { load(); }, [load]);

  const handleFiles = async (files) => {
    const pdfs = Array.from(files).filter(f => f.type === 'application/pdf');
    if (!pdfs.length) return toast.error('Seuls les fichiers PDF sont acceptés');
    setUploading(true);
    let ok = 0;
    for (const file of pdfs) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        await api.uploadPieceJointe(materiel.materiel_id, fd);
        ok++;
      } catch (err) {
        toast.error(`Erreur pour ${file.name} : ${err.response?.data?.detail || 'Erreur'}`);
      }
    }
    if (ok > 0) { toast.success(`${ok} fichier(s) ajouté(s)`); await load(); }
    setUploading(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDownload = async (pj) => {
    try {
      const r = await api.downloadPieceJointe(pj.pj_id);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${pj.nom}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleDelete = async (pj) => {
    try {
      await api.deletePieceJointe(pj.pj_id);
      setPjs(prev => prev.filter(p => p.pj_id !== pj.pj_id));
      toast.success('Supprimé');
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const startEdit = (pj) => { setEditingId(pj.pj_id); setEditingNom(pj.nom); };

  const saveEdit = async (pj) => {
    if (!editingNom.trim()) return;
    try {
      await api.renamePieceJointe(pj.pj_id, editingNom.trim());
      setPjs(prev => prev.map(p => p.pj_id === pj.pj_id ? { ...p, nom: editingNom.trim() } : p));
      setEditingId(null);
    } catch {
      toast.error('Erreur lors du renommage');
    }
  };

  const fmtSize = (b) => b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} Mo` : `${Math.round(b / 1024)} Ko`;

  return (
    <div className="space-y-4 py-1">
      {/* Zone de dépôt */}
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        data-testid="pj-dropzone"
        className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all"
        style={{
          borderColor: isDragging ? 'var(--primary-solid)' : 'var(--border-light)',
          background: isDragging ? 'var(--primary-50)' : 'var(--surface-1)',
        }}
      >
        <input ref={fileInputRef} type="file" accept=".pdf,application/pdf" multiple className="hidden"
          onChange={e => handleFiles(e.target.files)} />
        <div className="flex flex-col items-center gap-2">
          {uploading
            ? <div className="animate-spin rounded-full h-7 w-7 border-2 border-t-transparent" style={{ borderColor: 'var(--primary-solid)' }} />
            : <Paperclip size={28} style={{ color: isDragging ? 'var(--primary-solid)' : 'var(--text-muted)' }} />
          }
          <p className="text-sm font-medium" style={{ color: isDragging ? 'var(--primary-solid)' : 'var(--text-muted)' }}>
            {uploading ? 'Envoi en cours...' : 'Glissez vos PDF ici ou cliquez pour sélectionner'}
          </p>
          <p className="text-xs" style={{ color: 'var(--text-disabled)' }}>PDF uniquement · 20 Mo max par fichier</p>
        </div>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex justify-center py-6">
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-t-transparent" style={{ borderColor: 'var(--primary-solid)' }} />
        </div>
      ) : pjs.length === 0 ? (
        <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>
          <FileText size={32} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">Aucune pièce jointe</p>
        </div>
      ) : (
        <div className="space-y-2">
          {pjs.map(pj => (
            <div key={pj.pj_id}
              data-testid={`pj-item-${pj.pj_id}`}
              className="flex items-center gap-3 px-4 py-3 rounded-xl border"
              style={{ borderColor: 'var(--border-light)', background: 'white' }}>
              <FileText size={18} style={{ color: '#EF4444', flexShrink: 0 }} />

              {/* Nom éditable */}
              <div className="flex-1 min-w-0">
                {editingId === pj.pj_id ? (
                  <input
                    autoFocus
                    value={editingNom}
                    onChange={e => setEditingNom(e.target.value)}
                    onBlur={() => saveEdit(pj)}
                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(pj); if (e.key === 'Escape') setEditingId(null); }}
                    className="w-full px-2 py-1 text-sm rounded border outline-none"
                    style={{ borderColor: 'var(--primary-solid)', color: 'var(--text-primary)' }}
                    data-testid={`pj-rename-input-${pj.pj_id}`}
                  />
                ) : (
                  <div>
                    <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{pj.nom}</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{fmtSize(pj.size)} · {new Date(pj.created_at).toLocaleDateString('fr-FR')}</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => startEdit(pj)} title="Renommer"
                  data-testid={`pj-rename-btn-${pj.pj_id}`}
                  className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                  style={{ color: 'var(--text-muted)' }}>
                  <Pencil size={14} />
                </button>
                <button onClick={() => handleDownload(pj)} title="Télécharger"
                  data-testid={`pj-download-btn-${pj.pj_id}`}
                  className="p-1.5 rounded-lg hover:bg-blue-50 transition-colors"
                  style={{ color: 'var(--primary-solid)' }}>
                  <Download size={14} />
                </button>
                <button onClick={() => handleDelete(pj)} title="Supprimer"
                  data-testid={`pj-delete-btn-${pj.pj_id}`}
                  className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                  style={{ color: '#EF4444' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── ChauffeurSection ─────────────────────────────────────────────────────────
function ChauffeurSection({ materiel, onRefresh }) {
  const [data, setData] = useState({ courant: null, historique: [] });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    if (!materiel?.materiel_id) return;
    setLoading(true);
    try { const r = await api.getChauffeur(materiel.materiel_id); setData(r.data); }
    catch {} finally { setLoading(false); }
  }, [materiel?.materiel_id]);

  useEffect(() => { load(); }, [load]);

  // Recherche des employés avec la fonction interne "Chauffeur"
  useEffect(() => {
    if (!showForm) return;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.getEmployes({ fonction_interne: 'Chauffeur', statut: 'actif' });
        const all = Array.isArray(r.data) ? r.data : [];
        const q = search.toLowerCase();
        setResults(q ? all.filter(e => {
          const nom = `${e.personne?.prenom || ''} ${e.personne?.nom || ''}`.toLowerCase();
          return nom.includes(q);
        }) : all);
      } catch {} finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [search, showForm]);

  useEffect(() => {
    if (showForm && inputRef.current) inputRef.current.focus();
  }, [showForm]);

  const handleAssign = async (p) => {
    setSaving(true);
    try {
      // p est maintenant un Employé (personne_id + personne.prenom/nom)
      const nom = `${p.personne?.prenom || ''} ${p.personne?.nom || ''}`.trim()
                  || `${p.prenom || ''} ${p.nom || ''}`.trim();
      await api.assignerChauffeur(materiel.materiel_id, {
        personne_id: p.employe_id || p.personne_id,
        personne_nom: nom,
        note: note || null,
      });
      toast.success('Chauffeur affecté');
      setShowForm(false); setSearch(''); setNote('');
      await load();
      if (onRefresh) onRefresh();
    } catch { toast.error("Erreur lors de l'affectation"); }
    finally { setSaving(false); }
  };

  const handleRetirer = async () => {
    setSaving(true);
    try {
      await api.retirerChauffeur(materiel.materiel_id);
      toast.success('Chauffeur retiré');
      await load();
      if (onRefresh) onRefresh();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const fmtDate = (s) => s ? new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const { courant, historique } = data;

  return (
    <div className="space-y-3">
      {/* Chauffeur actuel */}
      <div className="rounded-xl border p-4" style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'var(--text-muted)' }}>Chauffeur affecté</p>
            {loading ? (
              <div className="w-20 h-4 rounded animate-pulse" style={{ background: 'var(--border)' }} />
            ) : courant ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold"
                style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)', border: '1px solid var(--primary-100)' }}>
                <User size={13} />{courant.personne_nom}
              </span>
            ) : (
              <span className="text-sm italic" style={{ color: 'var(--text-placeholder)' }}>Aucun chauffeur affecté</span>
            )}
          </div>
          <div className="flex gap-2">
            {courant && (
              <button type="button" onClick={handleRetirer} disabled={saving}
                className="px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-40"
                style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}
                data-testid="retirer-chauffeur-btn">
                Retirer
              </button>
            )}
            <button type="button" data-testid="affecter-chauffeur-btn"
              onClick={() => { setShowForm(true); setSearch(''); setNote(''); }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white flex-shrink-0"
              style={{ background: 'var(--primary-solid)' }}>
              <User size={13} /> {courant ? 'Changer de chauffeur' : 'Affecter'}
            </button>
          </div>
        </div>
        {courant?.note && (
          <p className="text-xs mt-2 pt-2 border-t italic" style={{ color: 'var(--text-muted)', borderColor: 'var(--border)' }}>
            {courant.note}
          </p>
        )}
      </div>

      {/* Formulaire de sélection */}
      {showForm && (
        <div className="rounded-xl border p-4 space-y-3" style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}>
          <p className="text-sm font-semibold" style={{ color: 'var(--text)',  }}>
            Sélectionner un chauffeur
          </p>
          <input ref={inputRef} type="text" value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher un contact avec la fonction Chauffeur…"
            className="w-full px-3 py-2 rounded-lg border text-sm focus:outline-none"
            style={{ borderColor: 'var(--border)', background: 'var(--bg)' }}
            data-testid="chauffeur-search-input" />
          <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border)', background: 'var(--bg)' }}>
            {searching ? (
              <p className="px-4 py-3 text-xs text-center" style={{ color: 'var(--text-muted)' }}>Recherche…</p>
            ) : results.length === 0 ? (
              <div className="px-4 py-4 text-center">
                <p className="text-xs font-medium mb-0.5" style={{ color: 'var(--text-muted)' }}>Aucun employé avec la fonction "Chauffeur"</p>
                <p className="text-xs" style={{ color: 'var(--text-disabled)' }}>
                  Attribuez la fonction "Chauffeur" à un employé dans Ressources humaines → Employés
                </p>
              </div>
            ) : (
              results.map(p => {
                const nom = `${p.personne?.prenom || p.prenom || ''} ${p.personne?.nom || p.nom || ''}`.trim();
                const key = p.employe_id || p.personne_id;
                return (
                  <button key={key} type="button" onClick={() => handleAssign(p)}
                    className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center gap-3 border-b last:border-0 transition-colors"
                    style={{ borderColor: 'var(--border)' }} data-testid={`chauffeur-option-${key}`}>
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ background: 'var(--border)', color: 'var(--text-muted)' }}>
                      {(nom[0] || '?').toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{nom}</p>
                      {(p.personne?.fonction || p.fonction) && (
                        <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{p.personne?.fonction || p.fonction}</p>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
              Note (optionnel)
            </label>
            <input type="text" value={note} onChange={e => setNote(e.target.value)}
              placeholder="Ex : Affectation temporaire, zone Nord…"
              className="w-full px-3 py-2 rounded-lg border text-sm focus:outline-none"
              style={{ borderColor: 'var(--border)', background: 'var(--bg)' }} />
          </div>
          <button type="button" onClick={() => setShowForm(false)}
            className="text-xs px-3 py-1.5 rounded-lg" style={{ color: 'var(--text-muted)' }}>
            Annuler
          </button>
        </div>
      )}

      {/* Historique */}
      {historique.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
            Historique des chauffeurs
          </p>
          <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
            {historique.map((h, i) => (
              <div key={h.id} className="px-4 py-3 flex items-center gap-3 border-b last:border-0"
                style={{ borderColor: 'var(--border-light)' }}>
                <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: 'var(--primary-50)' }}>
                  <User size={12} style={{ color: 'var(--primary-solid)' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{h.personne_nom}</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {fmtDate(h.date_debut)} → {h.date_fin ? fmtDate(h.date_fin) : <span>en cours</span>}
                  </p>
                  {h.note && <p className="text-xs italic mt-0.5" style={{ color: 'var(--text-muted)' }}>{h.note}</p>}
                </div>
                {h.date_fin === null && (
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)', border: '1px solid var(--primary-100)' }}>actuel</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ─── ParcTab ──────────────────────────────────────────────────────────────────

function ParcTab({ materiel, onRefresh }) {
  const [mouvements, setMouvements] = useState([]);
  const [transferts, setTransferts] = useState([]);
  const [loadingMvt, setLoadingMvt] = useState(true);
  const [agencesSociete, setAgencesSociete] = useState([]);
  const [agenceNomCourant, setAgenceNomCourant] = useState(null);

  // Parc change form
  const [showForm, setShowForm] = useState(false);
  const [newParc, setNewParc] = useState('');
  const [newClient, setNewClient] = useState(null);
  const [commentaire, setCommentaire] = useState('');
  const [dateMouvement, setDateMouvement] = useState('');
  const [saving, setSaving] = useState(false);

  // Transfert agence form
  const [showTransfertForm, setShowTransfertForm] = useState(false);
  const [agenceDest, setAgenceDest] = useState('');
  const [motifTransfert, setMotifTransfert] = useState('');
  const [savingTransfert, setSavingTransfert] = useState(false);

  const loadAll = useCallback(async () => {
    if (!materiel?.materiel_id) { setLoadingMvt(false); return; }
    setLoadingMvt(true);
    try {
      const [mvtRes, trfRes] = await Promise.all([
        api.getMouvementsParc(materiel.materiel_id),
        api.getTransfertsAgence(materiel.materiel_id),
      ]);
      setMouvements(Array.isArray(mvtRes.data) ? mvtRes.data : []);
      setTransferts(Array.isArray(trfRes.data) ? trfRes.data : []);
    } catch {}
    finally { setLoadingMvt(false); }
  }, [materiel?.materiel_id]);

  // Charger les agences de la société (pour nom de l'agence courante + liste pour transfert)
  useEffect(() => {
    if (!materiel?.societe_id) return;
    api.getAgences({ societe_id: materiel.societe_id })
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : [];
        setAgencesSociete(list);
        const courant = list.find(a => a.agence_id === materiel.agence_id);
        setAgenceNomCourant(courant?.nom || null);
      })
      .catch(() => {});
  }, [materiel?.societe_id, materiel?.agence_id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Charger les agences de la même société pour le formulaire de transfert
  const openTransfertForm = async () => {
    setAgenceDest('');
    setMotifTransfert('');
    setShowForm(false);
    setShowTransfertForm(true);
  };

  const openForm = () => {
    setNewParc(''); setNewClient(null); setCommentaire('');
    setDateMouvement(new Date().toISOString().slice(0, 10));
    setShowTransfertForm(false);
    setShowForm(true);
  };

  const handleChangerParc = async () => {
    if (!newParc) { toast.error('Sélectionnez un parc de destination'); return; }
    if (newParc === 'client' && !newClient) { toast.error('Sélectionnez un client dans l\'annuaire'); return; }
    setSaving(true);
    try {
      await api.changerParcMateriel(materiel.materiel_id, {
        parc: newParc,
        client_id: newParc === 'client' ? newClient?.id : null,
        client_nom: newParc === 'client' ? newClient?.nom : null,
        commentaire: commentaire || null,
        date: dateMouvement || null,
      });
      toast.success('Changement de parc enregistré');
      setShowForm(false);
      await loadAll();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du changement de parc');
    } finally { setSaving(false); }
  };

  const handleTransferer = async () => {
    if (!agenceDest) { toast.error('Sélectionnez une agence de destination'); return; }
    if (!motifTransfert.trim()) { toast.error('Le motif est obligatoire'); return; }
    setSavingTransfert(true);
    try {
      await api.transfererAgence(materiel.materiel_id, {
        agence_destination_id: agenceDest,
        motif: motifTransfert,
      });
      toast.success('Transfert enregistré');
      setShowTransfertForm(false);
      await loadAll();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du transfert');
    } finally { setSavingTransfert(false); }
  };

  if (!materiel?.materiel_id) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <Layers size={32} className="mb-3" style={{ color: 'var(--text-disabled)' }} />
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Enregistrez d'abord le matériel pour gérer son parc.</p>
      </div>
    );
  }

  const currentParc = materiel?.parc;
  const currentParcCfg = PARCS[currentParc];
  const fmtDate = (s) => s ? new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  const btnPrimary = "flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white flex-shrink-0";
  const btnPrimaryStyle = { background: 'var(--primary-solid)' };
  const cardStyle = { background: 'var(--bg)', borderColor: 'var(--border)' };
  const chipStyle = { background: 'var(--primary-50)', color: 'var(--primary-solid)', border: '1px solid var(--primary-100)' };

  return (
    <div className="space-y-4">

      {/* ── Parc actuel ── */}
      <div className="rounded-xl border p-4" style={cardStyle}>
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'var(--text-muted)' }}>Parc actuel</p>
            {currentParcCfg ? (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold"
                  style={{ background: currentParcCfg.bg, color: currentParcCfg.color, border: `1px solid ${currentParcCfg.border}` }}>
                  <Layers size={13} />
                  {currentParcCfg.label}
                </span>
                {currentParc === 'client' && materiel.client_nom && (
                  <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                    — {materiel.client_nom}
                  </span>
                )}
              </div>
            ) : (
              <span className="text-sm italic" style={{ color: 'var(--text-placeholder)' }}>Non affecté</span>
            )}
          </div>
          <button type="button" data-testid="changer-parc-btn"
            onClick={openForm}
            className={btnPrimary}
            style={btnPrimaryStyle}>
            <Layers size={14} /> Changer de parc
          </button>
        </div>
      </div>

      {/* ── Agence actuelle ── */}
      <div className="rounded-xl border p-4" style={cardStyle}>
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'var(--text-muted)' }}>Agence actuelle</p>
            {(agenceNomCourant || materiel.agence_nom || materiel.agence_id) ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold" style={chipStyle}>
                <MapPin size={12} />
                {agenceNomCourant || materiel.agence_nom || materiel.agence_id}
              </span>
            ) : (
              <span className="text-sm italic" style={{ color: 'var(--text-placeholder)' }}>Aucune agence</span>
            )}
          </div>
          {materiel?.societe_id ? (
            <button type="button" data-testid="transferer-agence-btn"
              onClick={openTransfertForm}
              className={btnPrimary}
              style={btnPrimaryStyle}>
              <ArrowRightLeft size={14} /> Changer d'agence
            </button>
          ) : (
            <span className="text-xs px-3 py-1.5 rounded-lg"
              style={{ background: '#FEF9EE', color: '#92400E', border: '1px solid #FDE68A' }}>
              Rattacher à une société d'abord
            </span>
          )}
        </div>
      </div>

      {/* ── Chauffeur ── */}
      <ChauffeurSection materiel={materiel} onRefresh={onRefresh} />

      {/* ── Formulaire changement de parc ── */}
      {showForm && (
        <div className="rounded-xl border p-4 space-y-3" style={{ borderColor: 'var(--border)', background: '#FAFFF8' }}>
          <p className="text-sm font-semibold" style={{ color: 'var(--text)',  }}>
            Affecter à un nouveau parc
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="parc-selector">
            {Object.entries(PARCS).map(([key, cfg]) => (
              <button key={key} type="button"
                data-testid={`parc-option-${key}`}
                onClick={() => { setNewParc(key); if (key !== 'client') setNewClient(null); }}
                className="flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all"
                style={{
                  borderColor: newParc === key ? cfg.color : 'var(--border)',
                  background: newParc === key ? cfg.bg : 'white',
                  color: newParc === key ? cfg.color : 'var(--text-secondary)',
                }}>
                {newParc === key && <Check size={12} style={{ flexShrink: 0 }} />}
                {cfg.label === 'Location C.D.' ? 'Location courte durée' : cfg.label}
              </button>
            ))}
          </div>
          {newParc === 'client' && (
            <EntrepriseSearchField label="Client (Annuaire)" value={newClient} onChange={v => setNewClient(v)} />
          )}
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
              Commentaire (optionnel)
            </label>
            <input type="text" value={commentaire}
              onChange={e => setCommentaire(e.target.value)}
              data-testid="parc-commentaire-input"
              placeholder="Raison du changement, contexte..."
              className="w-full px-3 py-2 rounded-lg border text-sm focus:outline-none"
              style={{ borderColor: 'var(--border)' }} />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
              Date du mouvement
            </label>
            <input type="date" value={dateMouvement}
              onChange={e => setDateMouvement(e.target.value)}
              data-testid="parc-date-mouvement-input"
              max={new Date().toISOString().slice(0, 10)}
              className="w-full px-3 py-2 rounded-lg border text-sm focus:outline-none"
              style={{ borderColor: 'var(--border)' }} />
          </div>
          <div className="flex gap-2 pt-1">
            <button type="button" data-testid="confirm-changer-parc-btn"
              onClick={handleChangerParc}
              disabled={saving || !newParc}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-40"
              style={{ background: 'var(--primary-gradient)' }}>
              {saving ? 'Enregistrement...' : 'Confirmer le changement'}
            </button>
            <button type="button" onClick={() => setShowForm(false)}
              className="px-4 py-2.5 rounded-lg text-sm" style={{ color: 'var(--text-muted)' }}>
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* ── Formulaire transfert d'agence ── */}
      {showTransfertForm && (
        <div className="rounded-xl border p-4 space-y-3"
          style={{ borderColor: 'var(--primary-100)', background: 'var(--primary-50)' }}>
          <p className="text-sm font-semibold flex items-center gap-2"
            style={{ color: 'var(--primary-solid)',  }}>
            <ArrowRightLeft size={15} /> Transférer vers une autre agence
          </p>

          {agencesSociete.filter(a => a.agence_id !== materiel.agence_id).length === 0 ? (
            <div className="text-sm py-2" style={{ color: 'var(--text-muted)' }}>
              Aucune autre agence disponible dans cette société.
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide"
                  style={{ color: 'var(--text-muted)' }}>Agence de destination *</label>
                <select
                  data-testid="agence-dest-select"
                  value={agenceDest}
                  onChange={e => setAgenceDest(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none appearance-none"
                  style={{ borderColor: 'var(--border)', background: 'white' }}>
                  <option value="">Sélectionner une agence...</option>
                  {agencesSociete.filter(a => a.agence_id !== materiel.agence_id).map(a => (
                    <option key={a.agence_id} value={a.agence_id}>{a.nom}{a.ville ? ` — ${a.ville}` : ''}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide"
                  style={{ color: 'var(--text-muted)' }}>Motif du transfert *</label>
                <input type="text"
                  data-testid="motif-transfert-input"
                  value={motifTransfert}
                  onChange={e => setMotifTransfert(e.target.value)}
                  placeholder="Ex : Renforcement équipe, demande client, rotation stock..."
                  className="w-full px-3 py-2 rounded-lg border text-sm focus:outline-none"
                  style={{ borderColor: 'var(--border)', background: 'white' }} />
              </div>

              <div className="flex gap-2 pt-1">
                <button type="button" data-testid="confirm-transfert-btn"
                  onClick={handleTransferer}
                  disabled={savingTransfert || !agenceDest || !motifTransfert.trim()}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-40"
                  style={{ background: 'var(--primary-gradient)' }}>
                  {savingTransfert ? 'Transfert...' : 'Confirmer le transfert'}
                </button>
                <button type="button" onClick={() => setShowTransfertForm(false)}
                  className="px-4 py-2.5 rounded-lg text-sm" style={{ color: 'var(--text-muted)' }}>
                  Annuler
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Historique mouvements de parc ── */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
          Historique des mouvements de parc
        </p>
        {loadingMvt ? (
          <div className="flex justify-center py-6">
            <div className="animate-spin w-5 h-5 border-2 border-t-transparent rounded-full"
              style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
          </div>
        ) : mouvements.length === 0 ? (
          <div className="rounded-xl border p-4 text-center" style={{ borderColor: 'var(--border)' }}>
            <p className="text-sm" style={{ color: 'var(--text-placeholder)' }}>Aucun mouvement enregistré</p>
          </div>
        ) : (
          <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
            {mouvements.map((mvt) => {
              const srcCfg = PARCS[mvt.parc_source];
              const dstCfg = PARCS[mvt.parc_destination];
              return (
                <div key={mvt.mouvement_id} className="px-4 py-3 flex items-start gap-3 border-b last:border-0"
                  style={{ borderColor: 'var(--border-light)' }}>
                  <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ background: dstCfg?.bg || '#F3F4F6' }}>
                    <Layers size={12} style={{ color: dstCfg?.color || '#9CA3AF' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {srcCfg ? (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                          style={{ background: srcCfg.bg, color: srcCfg.color }}>{srcCfg.label}</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                          style={{ background: '#F1F5F9', color: '#94A3B8' }}>Non affecté</span>
                      )}
                      <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>→</span>
                      {dstCfg && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                          style={{ background: dstCfg.bg, color: dstCfg.color }}>{dstCfg.label}</span>
                      )}
                      {mvt.client_nom && (
                        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>— {mvt.client_nom}</span>
                      )}
                    </div>
                    {mvt.commentaire && (
                      <p className="text-xs mt-0.5 italic" style={{ color: 'var(--text-muted)' }}>{mvt.commentaire}</p>
                    )}
                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-placeholder)' }}>
                      {fmtDate(mvt.date)}{mvt.user_nom ? ` · ${mvt.user_nom}` : ''}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Historique transferts d'agence ── */}
      {(transferts.length > 0 || !loadingMvt) && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
            Historique des transferts d'agence
          </p>
          {transferts.length === 0 ? (
            <div className="rounded-xl border p-4 text-center" style={{ borderColor: 'var(--border)' }}>
              <p className="text-sm" style={{ color: 'var(--text-placeholder)' }}>Aucun transfert enregistré</p>
            </div>
          ) : (
            <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
              {transferts.map(trf => (
                <div key={trf.transfert_id} className="px-4 py-3 flex items-start gap-3 border-b last:border-0"
                  style={{ borderColor: 'var(--border-light)' }}>
                  <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ background: 'var(--primary-50)' }}>
                    <ArrowRightLeft size={12} style={{ color: 'var(--primary-solid)' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap text-xs">
                      <span className="px-2 py-0.5 rounded-full font-medium"
                        style={{ background: '#F1F5F9', color: '#475569' }}>
                        {trf.agence_source_nom || 'Aucune agence'}
                      </span>
                      <span style={{ color: 'var(--text-muted)' }}>→</span>
                      <span className="px-2 py-0.5 rounded-full font-medium"
                        style={{ background: 'var(--primary-100)', color: 'var(--primary-solid)' }}>
                        {trf.agence_destination_nom}
                      </span>
                    </div>
                    <p className="text-xs mt-0.5 italic" style={{ color: 'var(--text-secondary)' }}>{trf.motif}</p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-placeholder)' }}>
                      {fmtDate(trf.date)}{trf.user_nom ? ` · ${trf.user_nom}` : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
}



// Helper cellule inline (label gauche + champ droit) — défini hors du composant pour éviter le remontage
function Cell({ label, required, full, children }) {
  return (
    <div className={`flex items-start gap-2 py-2${full ? ' col-span-2' : ''}`}
      style={{ borderBottom: '1px solid var(--border-light)' }}>
      <span className="text-xs font-semibold uppercase tracking-wide pt-2.5 w-24 flex-shrink-0 leading-tight"
        style={{ color: 'var(--text-muted)',  }}>
        {label}{required && <span style={{ color: '#DC2626' }}>*</span>}
      </span>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

function MaterielPanel({ open, onOpenChange, materiel, onSave, onDelete, isReadOnly, onRefresh }) {
  const emptyForm = {
    nom: '', numero_parc: '', immatriculation: '',
    marque_id: null, marque: '', marque_label: '',
    modele_id: null, modele: '', modele_label: '',
    numero_serie: '', annee: '', statut: 'disponible', compteur_horaire: '',
    prix_achat: '', valeur_actuelle: '', date_achat: '', description: '', photos: [],
    proprietaire: null, exploitant: null,
    numero_immobilisation: '',
    article_id: null,
    suivi_preventif_actif: false,
    refus_suivi_preventif: false,
    prospection_commerciale: false,
    maintenance_id: null,
  };
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [marques, setMarques] = useState([]);
  const [modeles, setModeles] = useState([]);
  const [loadingModeles, setLoadingModeles] = useState(false);
  const [activeTab, setActiveTab] = useState('infos');
  const [articlesMateriels, setArticlesMateriels] = useState([]);
  const [familles, setPanelFamilles] = useState([]);
  const [sousFamilles, setPanelSousFamilles] = useState([]);
  // Suivre le materiel_id précédent pour ne réinitialiser l'onglet qu'au changement de matériel
  const prevMaterielIdRef = useRef(null);

  const { hasActivity } = useHasActivite('materiel', materiel?.materiel_id);

  // Charger les données de référence quand le panel s'ouvre
  useEffect(() => {
    if (!open) return;
    api.getMarques().then(r => setMarques(r.data.map(m => ({ id: m.marque_id, label: m.nom })))).catch(() => {});
    Promise.all([api.getFamilles(), api.getSousFamilles()])
      .then(([f, sf]) => { setPanelFamilles(Array.isArray(f.data) ? f.data : []); setPanelSousFamilles(Array.isArray(sf.data) ? sf.data : []); })
      .catch(() => {});

    const newId = materiel?.materiel_id ?? null;
    const idChanged = newId !== prevMaterielIdRef.current;
    prevMaterielIdRef.current = newId;

    if (materiel) {
      setForm({
        nom: materiel.nom || '',
        numero_parc: materiel.numero_parc || materiel.reference || '',
        immatriculation: materiel.immatriculation || '',
        marque_id: materiel.marque_id || null, marque: materiel.marque || '',
        marque_label: materiel.marque || '',
        modele_id: materiel.modele_id || null, modele: materiel.modele || '',
        modele_label: materiel.modele || '',
        numero_serie: materiel.numero_serie || '', annee: materiel.annee || '',
        statut: materiel.statut || 'disponible',
        compteur_horaire: materiel.compteur_horaire || '', prix_achat: materiel.prix_achat || '',
        cout_revient: materiel.cout_revient ?? null,
        valeur_actuelle: materiel.valeur_actuelle || '', date_achat: materiel.date_achat || '',
        description: materiel.description || '', photos: materiel.photos || [],
        has_compteur_km: materiel.has_compteur_km || false,
        has_compteur_h: materiel.has_compteur_h || false,
        proprietaire: materiel.proprietaire_id
          ? { id: materiel.proprietaire_id, nom: materiel.proprietaire_nom, type: materiel.proprietaire_type || 'entreprise' }
          : materiel.proprietaire_nom ? { id: null, nom: materiel.proprietaire_nom } : null,
        exploitant: materiel.exploitant_id
          ? { id: materiel.exploitant_id, nom: materiel.exploitant_nom, type: materiel.exploitant_type || 'entreprise' }
          : materiel.exploitant_nom ? { id: null, nom: materiel.exploitant_nom } : null,
        numero_immobilisation: materiel.numero_immobilisation || '',
        article_id: materiel.article_id || null,
        suivi_preventif_actif: materiel.suivi_preventif_actif || false,
        refus_suivi_preventif: materiel.refus_suivi_preventif || false,
        prospection_commerciale: materiel.prospection_commerciale || false,
        maintenance_id: materiel.maintenance_id || null,
      });
    } else {
      setForm({ ...emptyForm, has_compteur_km: false, has_compteur_h: false });
    }
    // Réinitialiser l'onglet uniquement quand on ouvre un matériel DIFFÉRENT
    if (idChanged) setActiveTab('infos');
  }, [materiel, open]);

  // Charger les articles une seule fois au montage (évite la race condition panel)
  useEffect(() => {
    api.getArticles({ type: 'materiel', slim: true }).then(r => setArticlesMateriels(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  // Charger les modèles quand la marque change
  useEffect(() => {
    if (!form.marque_id) { setModeles([]); return; }
    setLoadingModeles(true);
    api.getModeles({ marque_id: form.marque_id })
      .then(r => setModeles(r.data.map(m => ({ id: m.modele_id, label: m.nom }))))
      .catch(() => {})
      .finally(() => setLoadingModeles(false));
  }, [form.marque_id]);

  const handleCreateMarque = async (nom) => {
    const res = await api.createMarque({ nom });
    const newOpt = { id: res.data.marque_id, label: res.data.nom };
    setMarques(prev => [...prev.filter(m => m.id !== newOpt.id), newOpt].sort((a, b) => a.label.localeCompare(b.label)));
    return newOpt;
  };

  const handleCreateModele = async (nom) => {
    if (!form.marque_id) { toast.error("Sélectionnez d'abord une marque"); throw new Error('no marque'); }
    const res = await api.createModele({ nom, marque_id: form.marque_id });
    const newOpt = { id: res.data.modele_id, label: res.data.nom };
    setModeles(prev => [...prev.filter(m => m.id !== newOpt.id), newOpt].sort((a, b) => a.label.localeCompare(b.label)));
    return newOpt;
  };

  const handleSave = async () => {
    if (!materiel && !form.numero_serie?.trim()) {
      toast.error('Le numéro de série est obligatoire');
      return;
    }
    setLoading(true);
    try {
      // compteur_horaire et valeur_actuelle sont gérés exclusivement par l'onglet Compteur
      // — les inclure ici créerait un relevé "Saisie manuelle" parasite à chaque sauvegarde
      const { compteur_horaire, valeur_actuelle, ...rest } = form;
      const data = {
        ...rest,
        annee: form.annee ? parseInt(form.annee) : null,
        prix_achat: form.prix_achat ? parseFloat(form.prix_achat) : null,
        marque: form.marque_label || form.marque || null,
        modele: form.modele_label || form.modele || null,
        proprietaire_id:   form.proprietaire?.id   || null,
        proprietaire_nom:  form.proprietaire?.nom  || null,
        proprietaire_type: form.proprietaire?.type || null,
        exploitant_id:     form.exploitant?.id     || null,
        exploitant_nom:    form.exploitant?.nom     || null,
        exploitant_type:   form.exploitant?.type   || null,
        article_id: form.article_id || null,
      };
      await onSave(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setLoading(false);
    }
  };

  const inp = "w-full px-3 py-2 rounded-lg border text-sm focus:outline-none";
  const inpStyle = { borderColor: 'var(--border)' };
  const roSt = { background: 'var(--bg-subtle)', borderColor: 'var(--border-light)', color: 'var(--text-secondary)' };

  // Héritage famille/sous-famille depuis l'article sélectionné
  const selectedArticle = articlesMateriels.find(a => a.article_id === form.article_id);
  const heritageFamille = selectedArticle ? familles.find(f => f.famille_id === selectedArticle.famille_id) : null;
  const heritageSousFamille = selectedArticle ? sousFamilles.find(sf => sf.sous_famille_id === selectedArticle.sous_famille_id) : null;

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={materiel ? (materiel.numero_serie || materiel.numero_parc || materiel.nom) : 'Nouveau matériel'}
      copyValue={materiel ? (materiel.numero_serie || materiel.numero_parc) : undefined}
      subtitle={materiel ? materiel.nom : 'Ajouter un équipement au parc matériel'}
      footer={
        <div className="flex items-center justify-between">
          {/* Supprimer — seulement en mode édition */}
          {materiel ? (
            <button type="button" onClick={() => onDelete(materiel)}
              disabled={isReadOnly}
              title={isReadOnly ? 'Mode lecture seule : plusieurs agences sélectionnées' : undefined}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ color: '#DC2626' }}>
              <Trash2 size={14} /> Supprimer
            </button>
          ) : <span />}
          <div className="flex gap-3">
            <button type="button" onClick={() => onOpenChange(false)}
              className="px-4 py-2.5 rounded-lg text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
              Annuler
            </button>
            <button data-testid="materiel-submit-btn" type="button" onClick={handleSave}
              disabled={loading || isReadOnly}
              title={isReadOnly ? 'Mode lecture seule : plusieurs agences sélectionnées' : undefined}
              className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: (loading || isReadOnly) ? 'var(--primary-light)' : 'var(--primary-gradient)' }}>
              {loading ? 'Enregistrement...' : (materiel ? 'Mettre à jour' : 'Créer')}
            </button>
          </div>
        </div>
      }
    >
      {/* Onglets */}
      <div className="flex border-b mb-4" style={{ borderColor: 'var(--border)' }}>
        {[
          { key: 'infos', label: 'Informations' },
          { key: 'parc', label: 'Parc' },
          { key: 'compteur', label: 'Compteur' },
          { key: 'financement', label: 'Financement' },
          { key: 'preventif', label: 'Préventif' },
          { key: 'pj', label: 'Documents' },
          { key: 'caracs', label: 'Caractéristiques' },
          { key: 'activites', label: 'Activités', icon: hasActivity ? <ActivityPulse /> : null },
        ].map(tab => (
          <button key={tab.key} type="button"
            onClick={() => setActiveTab(tab.key)}
            className="px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-1"
            style={{
              borderColor: activeTab === tab.key ? 'var(--primary-solid)' : 'transparent',
              color: activeTab === tab.key ? 'var(--primary-solid)' : 'var(--text-muted)',
            }}>
            {tab.label}{tab.icon}
          </button>
        ))}
      </div>

      {activeTab === 'caracs' ? (
        <MaterielCaracTab materielId={materiel?.materiel_id} />
      ) : activeTab === 'parc' ? (
        <ParcTab materiel={materiel} onRefresh={onRefresh} />
      ) : activeTab === 'compteur' ? (
        <CompteurTab materiel={materiel} form={form} setForm={setForm} />
      ) : activeTab === 'financement' ? (
        <FinancementTab materiel={materiel} form={form} setForm={setForm} />
      ) : activeTab === 'preventif' ? (
        <PreventifTab materiel={materiel} form={form} setForm={setForm} />
      ) : activeTab === 'pj' ? (
        <div className="p-4">
          <DocumentsTab
            entityId={materiel?.materiel_id}
            listFn={()                    => api.getMaterielDocuments(materiel.materiel_id)}
            uploadFn={(formData)          => api.uploadMaterielDocument(materiel.materiel_id, formData)}
            updateFn={(docId, data)       => api.patchMaterielDocument(materiel.materiel_id, docId, data)}
            deleteFn={(docId)             => api.deleteMaterielDocument(materiel.materiel_id, docId)}
            downloadFn={(docId)           => api.downloadMaterielDocument(materiel.materiel_id, docId)}
          />
        </div>
      ) : activeTab === 'activites' ? (
        materiel?.materiel_id ? (
          <ActivitesFichePanel
            record_type="materiel"
            record_id={materiel.materiel_id}
            record_label={materiel.numero_serie || materiel.numero_parc || materiel.nom}
            type_document="materiel"
            numero_document={materiel.numero_serie || materiel.numero_parc || materiel.nom}
            activiteFetcher={null}
          />
        ) : null
      ) : (
      <div className="grid grid-cols-2 gap-x-4">

        {/* N° de parc  |  Immatriculation */}
        <Cell label="N° parc">
          <input data-testid="materiel-numero-parc-input" type="text" className={inp} style={inpStyle}
            value={form.numero_parc} onChange={e => setForm({ ...form, numero_parc: e.target.value })}
            placeholder="TR-001" />
        </Cell>
        <Cell label="Immatricu.">
          <input data-testid="materiel-immatriculation-input" type="text" className={inp} style={inpStyle}
            value={form.immatriculation} onChange={e => setForm({ ...form, immatriculation: e.target.value })}
            placeholder="AB-123-CD" />
        </Cell>

        {/* Article rattaché — pleine largeur */}
        <Cell label="Article" full>
          <ArticleSearchDropdown
            value={form.article_id}
            options={articlesMateriels}
            onChange={async (a) => {
              if (!a) {
                setForm(f => ({
                  ...f, article_id: null,
                  nom: '',
                  marque_id: null, marque_label: '', marque: '',
                  modele_id: null, modele_label: '', modele: '',
                }));
                return;
              }
              const updates = { article_id: a.article_id };
              // Héritage nom depuis l'article
              if (a.designation) updates.nom = a.designation;
              // Héritage marque depuis l'article (toujours)
              if (a.marque_id) {
                const marque = marques.find(m => m.id === a.marque_id);
                if (marque) {
                  updates.marque_id = marque.id;
                  updates.marque_label = marque.label;
                  updates.marque = marque.label;
                  // Héritage modèle depuis l'article (toujours)
                  if (a.modele_id) {
                    updates.modele_id = a.modele_id;
                    try {
                      const res = await api.getModeles({ marque_id: a.marque_id });
                      const modele = res.data.find(m => m.modele_id === a.modele_id);
                      if (modele) { updates.modele_label = modele.nom; updates.modele = modele.nom; }
                    } catch {}
                  } else {
                    // L'article n'a pas de modèle → on efface le modèle du matériel
                    updates.modele_id = null; updates.modele_label = ''; updates.modele = '';
                  }
                }
              }
              setForm(f => ({ ...f, ...updates }));
            }}
          />
        </Cell>

        {/* Nom — pleine largeur, hérité de l'article si lié */}
        <Cell label="Nom" required full>
          {form.article_id ? (
            <div className="flex items-center gap-2">
              <input readOnly className={inp} style={roSt}
                data-testid="materiel-nom-input"
                value={form.nom}
                placeholder="—" />
              <Lock size={13} style={{ color: 'var(--text-disabled)', flexShrink: 0 }} title="Hérité de l'article" />
            </div>
          ) : (
            <input data-testid="materiel-nom-input" type="text" className={inp} style={inpStyle}
              value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })}
              placeholder="Ex: Tracteur John Deere 6120M" />
          )}
        </Cell>

        {/* Famille  |  Sous-famille — héritées, lecture seule */}
        <Cell label="Famille">
          <input readOnly className={inp} style={roSt}
            value={heritageFamille ? heritageFamille.nom : ''}
            placeholder="—" />
        </Cell>
        <Cell label="Ss-famille">
          <input readOnly className={inp} style={roSt}
            value={heritageSousFamille ? heritageSousFamille.nom : ''}
            placeholder="—" />
        </Cell>

        {/* Marque  |  Modèle — lecture seule si article sélectionné, sinon éditables */}
        <Cell label="Marque">
          {form.article_id ? (
            <div className="flex items-center gap-2">
              <input readOnly className={inp} style={roSt}
                data-testid="materiel-marque-select"
                value={form.marque_label || form.marque || ''}
                placeholder="—" />
              <Lock size={13} style={{ color: 'var(--text-disabled)', flexShrink: 0 }} title="Hérité de l'article" />
            </div>
          ) : (
            <ComboDropdown
              data-testid="materiel-marque-select"
              value={form.marque_id}
              valueLabel={form.marque_label}
              options={marques}
              placeholder="Marque..."
              onCreate={handleCreateMarque}
              onChange={(id, label) => setForm(f => ({
                ...f,
                marque_id: id, marque_label: label || '',
                modele_id: null, modele_label: '', modele: '',
              }))}
            />
          )}
        </Cell>
        <Cell label="Modèle">
          {form.article_id ? (
            <div className="flex items-center gap-2">
              <input readOnly className={inp} style={roSt}
                data-testid="materiel-modele-select"
                value={form.modele_label || form.modele || ''}
                placeholder="—" />
              <Lock size={13} style={{ color: 'var(--text-disabled)', flexShrink: 0 }} title="Hérité de l'article" />
            </div>
          ) : (
            <ComboDropdown
              data-testid="materiel-modele-select"
              value={form.modele_id}
              valueLabel={form.modele_label}
              options={modeles}
              placeholder={form.marque_id ? (loadingModeles ? '...' : 'Modèle...') : "Choisir une marque"}
              onCreate={form.marque_id ? handleCreateModele : undefined}
              onChange={(id, label) => setForm(f => ({ ...f, modele_id: id, modele_label: label || '' }))}
              disabled={!form.marque_id}
            />
          )}
        </Cell>

        {/* N° Série  |  Année */}
        <Cell label="N° Série">
          <input type="text" className={inp} style={inpStyle}
            value={form.numero_serie} onChange={e => setForm({ ...form, numero_serie: e.target.value })}
            placeholder="SN123456789" />
        </Cell>
        <Cell label="Année">
          <input type="number" className={inp} style={inpStyle}
            value={form.annee} onChange={e => setForm({ ...form, annee: e.target.value })}
            placeholder="2023" min="1900" max="2100" />
        </Cell>

        {/* État (calculé) | Compteur horaire */}
        {materiel && (
          <Cell label="État actuel">
            <div className="pt-1">
              <MatBadges badges={materiel.badges} size="md" showOk={true} />
            </div>
          </Cell>
        )}
        <Cell label="Compteur (h)">
          <div className={inp} style={{ ...inpStyle, background: 'var(--bg-subtle)', color: 'var(--text-secondary)', cursor: 'default' }}
            data-testid="materiel-compteur-h-display"
            title="Lecture seule — les relevés se saisissent dans l'onglet Compteur">
            {form.compteur_horaire !== '' && form.compteur_horaire != null
              ? `${Number(form.compteur_horaire).toLocaleString('fr-FR')} h`
              : '—'}
          </div>
        </Cell>

        {/* Prix achat  |  Prix de revient */}
        <Cell label="Prix achat">
          <div className="relative">
            <PriceInput className={`${inp} pr-7`} style={inpStyle}
              value={form.prix_achat} onChange={v => setForm({ ...form, prix_achat: v })}
              placeholder="50.000,00" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs pointer-events-none"
              style={{ color: 'var(--text-placeholder)' }}>€</span>
          </div>
        </Cell>
        <Cell label="Prix de revient">
          <div className="relative">
            <input
              type="text"
              readOnly
              className={`${inp} pr-7`}
              style={{ ...inpStyle, background: 'var(--bg-subtle)', color: form.cout_revient != null ? 'var(--text)' : 'var(--text-disabled)', cursor: 'default' }}
              value={form.cout_revient != null
                ? Number(form.cout_revient).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : '—'}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs pointer-events-none"
              style={{ color: 'var(--text-placeholder)' }}>€</span>
          </div>
          
        </Cell>

        {/* Valeur actu.  |  Date achat */}
        <Cell label="Valeur actu.">
          <div className="relative">
            <PriceInput className={`${inp} pr-7`} style={inpStyle}
              value={form.valeur_actuelle} onChange={v => setForm({ ...form, valeur_actuelle: v })}
              placeholder="35.000,00" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs pointer-events-none"
              style={{ color: 'var(--text-placeholder)' }}>€</span>
          </div>
        </Cell>
        <Cell label="Date achat">
          <input type="date" className={inp} style={inpStyle}
            value={form.date_achat} onChange={e => setForm({ ...form, date_achat: e.target.value })} />
        </Cell>

        {/* Description — pleine largeur */}
        <Cell label="Descr." full>
          <textarea className={inp} style={{ ...inpStyle, resize: 'none' }}
            value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
            rows={2} placeholder="Notes, équipements optionnels..." />
        </Cell>

        {/* Propriétaire — pleine largeur */}
        <Cell label="Propriétaire" full>
          <EntrepriseSearchField
            value={form.proprietaire}
            onChange={v => setForm(f => ({ ...f, proprietaire: v }))}
          />
        </Cell>

        {/* Exploitant — pleine largeur */}
        <Cell label="Exploitant" full>
          <EntrepriseSearchField
            value={form.exploitant}
            onChange={v => setForm(f => ({ ...f, exploitant: v }))}
          />
        </Cell>

      </div>
      )}
    </SidePanel>
  );
}

// ─── Import Panel (Side Panel) ────────────────────────────────────────────────
function ImportPanel({ open, onOpenChange, onImportComplete }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setFile(null); setPreview(null); setResult(null); };

  useEffect(() => { if (!open) reset(); }, [open]);

  const handleFileChange = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.name.endsWith('.csv')) { toast.error('Veuillez sélectionner un fichier CSV'); return; }
    setFile(f); setLoading(true); setResult(null);
    try {
      const res = await api.previewCsvImport(f);
      setPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la lecture du fichier');
      setFile(null);
    } finally { setLoading(false); }
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const res = await api.importCsv(file);
      setResult(res.data);
      if (res.data.imported > 0) { toast.success(`${res.data.imported} matériel(s) importé(s)`); onImportComplete(); }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'import');
    } finally { setImporting(false); }
  };

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      title="Importer des matériels"
      subtitle="Importation depuis un fichier CSV"
      size="lg"
      footer={
        <div className="flex justify-end gap-3">
          <button onClick={() => onOpenChange(false)}
            className="px-4 py-2.5 rounded-lg text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
            {result ? 'Fermer' : 'Annuler'}
          </button>
          {!result && preview && (
            <button data-testid="import-confirm-btn" onClick={handleImport}
              disabled={importing || preview.preview.length === 0}
              className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white"
              style={{ background: importing ? 'var(--primary-light)' : 'var(--primary-gradient)' }}>
              {importing ? 'Import en cours...' : `Importer ${preview.total_rows} ligne(s)`}
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        {!result && (
          <>
            <div className="border-2 border-dashed rounded-xl p-8 text-center transition-colors"
              style={{ borderColor: file ? 'var(--primary-solid)' : 'var(--text-disabled)', background: file ? 'var(--primary-50)' : 'transparent' }}>
              <input type="file" accept=".csv" onChange={handleFileChange}
                className="hidden" id="csv-upload" />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <Upload size={32} className="mx-auto mb-3" style={{ color: file ? 'var(--primary-solid)' : 'var(--text-placeholder)' }} />
                {file ? (
                  <p className="font-medium" style={{ color: 'var(--primary-solid)' }}>{file.name}</p>
                ) : (
                  <>
                    <p className="font-medium" style={{ color: 'var(--text-secondary)' }}>Cliquez pour sélectionner un fichier CSV</p>
                    <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>ou glissez-déposez</p>
                  </>
                )}
              </label>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Format CSV attendu :</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Colonnes : nom, référence, catégorie, marque, modèle, numéro série, année, statut, compteur_horaire, prix_achat, valeur_actuelle, date_achat, description
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                Séparateur : point-virgule (;) ou virgule (,) | Encodage : UTF-8 ou Latin-1
              </p>
            </div>

            {loading && (
              <div className="text-center py-8">
                <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full mx-auto"
                  style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
                <p className="mt-2 text-sm" style={{ color: 'var(--text-muted)' }}>Analyse du fichier...</p>
              </div>
            )}

            {preview && !loading && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                    Aperçu ({preview.total_rows} lignes détectées)
                  </p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {Object.keys(preview.columns_mapped).length} colonnes reconnues
                  </p>
                </div>
                {preview.errors.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-xs font-medium text-yellow-800">Avertissements :</p>
                    {preview.errors.slice(0, 3).map((err, i) => (
                      <p key={i} className="text-xs text-yellow-700 mt-1">{err}</p>
                    ))}
                  </div>
                )}
                <div className="overflow-x-auto border rounded-lg" style={{ borderColor: 'var(--border)' }}>
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        {['Nom', 'Référence', 'Catégorie', 'Marque', 'Statut'].map(h => (
                          <th key={h} className="px-3 py-2 text-left font-medium" style={{ color: 'var(--text-secondary)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.preview.map((row, i) => (
                        <tr key={i} className="border-t" style={{ borderColor: 'var(--border)' }}>
                          <td className="px-3 py-2" style={{ color: 'var(--text)' }}>{row.nom}</td>
                          <td className="px-3 py-2" style={{ color: 'var(--text-muted)' }}>{row.reference || '-'}</td>
                          <td className="px-3 py-2" style={{ color: 'var(--text-muted)' }}>{row.categorie || '-'}</td>
                          <td className="px-3 py-2" style={{ color: 'var(--text-muted)' }}>{row.marque || '-'}</td>
                          <td className="px-3 py-2" style={{ color: 'var(--text-muted)' }}>{row.statut || 'disponible'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {result && (
          <div className="text-center py-8">
            {result.imported > 0 ? (
              <>
                <CheckCircle size={48} className="mx-auto mb-3" style={{ color: '#22C55E' }} />
                <p className="text-lg font-bold" style={{ color: 'var(--text)',  }}>
                  {result.imported} matériel(s) importé(s)
                </p>
              </>
            ) : (
              <>
                <AlertCircle size={48} className="mx-auto mb-3" style={{ color: '#D97706' }} />
                <p className="text-lg font-bold" style={{ color: 'var(--text)',  }}>
                  Aucun matériel importé
                </p>
              </>
            )}
            {result.total_errors > 0 && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-left">
                <p className="text-xs font-medium text-red-800">{result.total_errors} erreur(s) :</p>
                {result.errors.map((err, i) => (
                  <p key={i} className="text-xs text-red-700 mt-1">{err}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </SidePanel>
  );
}

// ─── Sort helpers ─────────────────────────────────────────────────────────────
function sortMateriels(list, field, dir) {
  const val = (m) => {
    switch (field) {
      case 'numero_parc':    return (m.numero_parc || m.reference || '').toLowerCase();
      case 'immatriculation':return (m.immatriculation || '').toLowerCase();
      case 'nom':            return (m.nom || '').toLowerCase();
      case 'annee':          return m.annee || 0;
      case 'numero_serie':   return (m.numero_serie || '').toLowerCase();
      case 'statut':         return (m.statut || '').toLowerCase();
      case 'parc':           return (m.parc || '').toLowerCase();
      case 'compteur':       return m.compteur_horaire ?? -1;
      default:               return '';
    }
  };
  return [...list].sort((a, b) => {
    const va = val(a), vb = val(b);
    if (va < vb) return dir === 'asc' ? -1 : 1;
    if (va > vb) return dir === 'asc' ?  1 : -1;
    return 0;
  });
}

// ─── Sortable Header Cell ─────────────────────────────────────────────────────
function ThSort({ label, field, sortField, sortDir, onSort }) {
  const active = sortField === field;
  return (
    <th
      onClick={() => onSort(field)}
      title={label}
      className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide cursor-pointer select-none whitespace-nowrap overflow-hidden"
      style={{ color: active ? 'var(--primary-solid)' : 'var(--text-muted)' }}>
      <span className="inline-flex items-center gap-1 max-w-full">
        <span className="truncate">{label}</span>
        {active
          ? (sortDir === 'asc' ? <ChevronUp size={12} className="shrink-0" /> : <ChevronDown size={12} className="shrink-0" />)
          : <span className="opacity-30 shrink-0">↕</span>}
      </span>
    </th>
  );
}

// ─── Table Row ────────────────────────────────────────────────────────────────
// ─── FilterDropdown — Filtre multi-sélection avec checkboxes ─────────────────
function FilterDropdown({ label, options, selected, onChange, testId, searchable }) {
  const [open, setOpen]     = useState(false);
  const [query, setQuery]   = useState('');
  const ref       = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Auto-focus le champ de recherche à l'ouverture
  useEffect(() => {
    if (open && searchable) {
      setTimeout(() => searchRef.current?.focus(), 50);
    }
    if (!open) setQuery('');
  }, [open, searchable]);

  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  };

  const visibleOptions = searchable && query.trim()
    ? options.filter(o => normalizeStr(o.label).includes(normalizeStr(query)))
    : options;

  const n = selected.length;
  const isActive = n > 0;

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button onClick={() => setOpen(v => !v)} data-testid={testId}
        className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg border text-sm whitespace-nowrap"
        style={{
          borderColor: isActive ? 'var(--primary-solid)' : 'var(--text-disabled)',
          color: isActive ? 'var(--primary-solid)' : 'var(--text-secondary)',
          background: isActive ? 'var(--primary-50)' : 'white',
        }}>
        {label}
        {isActive && (
          <span className="px-1.5 py-0.5 rounded-full text-xs font-bold text-white"
            style={{ background: 'var(--primary-solid)' }}>{n}</span>
        )}
        <ChevronDown size={13} className={`transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white rounded-xl border shadow-xl z-40 py-1"
          style={{ borderColor: 'var(--border)', minWidth: '200px' }}>

          {/* Champ de recherche (searchable uniquement) */}
          {searchable && (
            <div className="px-2 pt-1.5 pb-1 border-b" style={{ borderColor: 'var(--border)' }}>
              <div className="relative">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
                  style={{ color: 'var(--text-placeholder)' }} />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Escape' && setOpen(false)}
                  placeholder="Rechercher…"
                  className="w-full pl-7 pr-3 py-1.5 rounded-lg border text-xs outline-none"
                  style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                  data-testid={`${testId}-search`}
                />
              </div>
            </div>
          )}

          {isActive && (
            <button onClick={() => onChange([])}
              className="w-full px-3 py-1.5 text-left text-xs flex items-center gap-1.5 hover:bg-gray-50"
              style={{ color: 'var(--text-muted)' }}>
              <X size={10} /> Tout effacer
            </button>
          )}

          <div className="overflow-y-auto" style={{ maxHeight: 'min(70vh, 360px)' }}>
            {visibleOptions.length === 0 ? (
              <p className="px-3 py-2 text-xs" style={{ color: 'var(--text-muted)' }}>Aucun résultat</p>
            ) : (
              visibleOptions.map(opt => (
                <label key={opt.value}
                  className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-gray-50">
                  <input type="checkbox" checked={selected.includes(opt.value)}
                    onChange={() => toggle(opt.value)}
                    className="w-3.5 h-3.5 rounded flex-shrink-0"
                    style={{ accentColor: 'var(--primary-solid)' }} />
                  {opt.dot && <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: opt.dot }} />}
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{opt.label}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MaterielRow ──────────────────────────────────────────────────────────────
function MaterielRow({ materiel, onOpen, selected, onToggle, articles, visibleCols }) {
  const statut = STATUTS[materiel.statut] || STATUTS.disponible;
  const StatutIcon = statut.icon;
  const numeroParc = materiel.numero_parc || materiel.reference;
  const empty = <span style={{ color: 'var(--text-disabled)' }}>—</span>;
  const show = (k) => visibleCols[k] !== false;
  const linkedArticle = (articles || []).find(a => a.article_id === materiel.article_id);

  return (
    <tr
      data-testid="materiel-row"
      className={`border-b cursor-pointer transition-colors group ${selected ? 'bg-blue-50/60' : 'hover:bg-gray-50'}`}
      style={{ borderColor: 'var(--border-light)' }}>
      {/* Checkbox */}
      <td className="pl-2 pr-1 py-3 overflow-hidden" onClick={e => e.stopPropagation()}>
        <input type="checkbox" checked={selected} onChange={onToggle}
          className="w-4 h-4 rounded cursor-pointer"
          style={{ accentColor: 'var(--primary-solid)' }} />
      </td>
      {show('numero_parc') && (
        <td className="px-4 py-3 text-sm overflow-hidden" onClick={() => onOpen(materiel)} data-testid="cell-identifiants">
          {materiel.numero_serie && (
            <div className="font-medium truncate" style={{ color: 'var(--text-secondary)' }} title={materiel.numero_serie}>{materiel.numero_serie}</div>
          )}
          {materiel.immatriculation && (
            <div className="text-xs mt-0.5 truncate" style={{ color: 'var(--text-muted)' }} title={materiel.immatriculation}>Immat. : {materiel.immatriculation}</div>
          )}
          <div className={`${materiel.numero_serie || materiel.immatriculation ? 'text-xs mt-0.5' : 'font-medium'} truncate`}
            style={{ color: materiel.numero_serie || materiel.immatriculation ? 'var(--text-muted)' : 'var(--text-secondary)' }}
            title={numeroParc || ''}>
            {numeroParc ? (materiel.numero_serie || materiel.immatriculation ? `N° parc : ${numeroParc}` : numeroParc) : empty}
          </div>
        </td>
      )}
      {show('article') && (
        <td className="px-4 py-3 text-sm whitespace-nowrap overflow-hidden" onClick={() => onOpen(materiel)} data-testid="cell-article">
          {linkedArticle ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono max-w-full truncate"
              style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)', border: '1px solid #BFDBFE' }}
              title={`${linkedArticle.reference} — ${linkedArticle.designation || ''}`}>
              {linkedArticle.reference}
            </span>
          ) : empty}
        </td>
      )}
      {show('nom') && (
        <td className="px-4 py-3 text-sm whitespace-nowrap overflow-hidden text-ellipsis" style={{ color: 'var(--text)' }} onClick={() => onOpen(materiel)}
          title={[materiel.nom, materiel.marque, materiel.modele].filter(Boolean).join(' ')}>
          {[materiel.nom, materiel.marque, materiel.modele].filter(Boolean).join(' ')}
        </td>
      )}
      {show('annee') && (
        <td className="px-4 py-3 text-sm whitespace-nowrap" style={{ color: 'var(--text-secondary)' }} onClick={() => onOpen(materiel)}>{materiel.annee || empty}</td>
      )}
      {show('badges') && (
        <td className="px-4 py-3 whitespace-nowrap overflow-hidden" onClick={() => onOpen(materiel)}>
          <MatBadges badges={materiel.badges} size="sm" />
        </td>
      )}
      {show('parc') && (
        <td className="px-4 py-3 whitespace-nowrap overflow-hidden" onClick={() => onOpen(materiel)}>
          {materiel.parc && PARCS[materiel.parc] ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium max-w-full truncate"
              style={{ background: PARCS[materiel.parc].bg, color: PARCS[materiel.parc].color }}>
              {PARCS[materiel.parc].label}
            </span>
          ) : empty}
        </td>
      )}
      {show('compteur') && (
        <td className="px-4 py-3 text-sm whitespace-nowrap overflow-hidden" style={{ color: 'var(--text-secondary)' }} onClick={() => onOpen(materiel)}>
          {materiel.compteur_horaire != null
            ? <span className="flex items-center gap-1"><Clock size={12} className="shrink-0" style={{ color: 'var(--text-placeholder)' }} />{materiel.compteur_horaire}h</span>
            : empty}
        </td>
      )}
      {show('proprietaire') && (
        <td className="px-4 py-3 text-sm overflow-hidden" style={{ color: 'var(--text-secondary)' }} onClick={() => onOpen(materiel)} data-testid="cell-exploitant-proprietaire">
          {materiel.exploitant_nom && (
            <div className="flex items-center gap-1 font-medium" style={{ color: 'var(--text-secondary)' }}
              title={`Exploitant : ${materiel.exploitant_nom}`}>
              {materiel.exploitant_type === 'personne' ? <User size={11} className="shrink-0" /> : <Building2 size={11} className="shrink-0" />}
              <span className="truncate">{materiel.exploitant_nom}</span>
            </div>
          )}
          {materiel.proprietaire_nom
            ? materiel.proprietaire_id
              ? <button type="button" onClick={e => {
                  e.stopPropagation();
                  const link = materiel.proprietaire_type === 'personne'
                    ? `/annuaire?personne=${materiel.proprietaire_id}`
                    : `/annuaire?entreprise=${materiel.proprietaire_id}`;
                  window.open(link, '_blank');
                }}
                  className={`flex items-center gap-1 hover:underline ${materiel.exploitant_nom ? 'text-xs mt-0.5' : ''} max-w-full`}
                  style={{ color: 'var(--primary-solid)' }}
                  title={`Propriétaire : ${materiel.proprietaire_nom}`}>
                  {materiel.proprietaire_type === 'personne' ? <User size={11} className="shrink-0" /> : <Building2 size={11} className="shrink-0" />}
                  <span className="truncate">{materiel.proprietaire_nom}</span>
                </button>
              : <span className={`flex items-center gap-1 ${materiel.exploitant_nom ? 'text-xs mt-0.5' : ''}`} style={{ color: 'var(--text-muted)' }}
                  title={`Propriétaire : ${materiel.proprietaire_nom}`}>
                  <Building2 size={11} className="shrink-0" /><span className="truncate">{materiel.proprietaire_nom}</span>
                </span>
            : (!materiel.exploitant_nom && empty)}
        </td>
      )}
      {show('dernier_comptage') && (
        <td className="px-4 py-3 whitespace-nowrap" onClick={() => onOpen(materiel)}>
          {materiel.dernier_comptage_statut ? (
            <div>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                style={{
                  background: materiel.dernier_comptage_statut === 'present' ? '#F0FDF4' : '#FEF2F2',
                  color: materiel.dernier_comptage_statut === 'present' ? '#16A34A' : '#DC2626',
                  border: `1px solid ${materiel.dernier_comptage_statut === 'present' ? '#86EFAC' : '#FECACA'}`,
                }}>
                {materiel.dernier_comptage_statut === 'present' ? 'Présent' : 'Introuvable'}
              </span>
              {materiel.dernier_comptage_date && (
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                  {new Date(materiel.dernier_comptage_date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </p>
              )}
            </div>
          ) : empty}
        </td>
      )}
      {/* Cellule vide alignée sur la th sélecteur de colonnes */}
      <td className="w-10 px-2" />
    </tr>
  );
}



// ─── Main Page ────────────────────────────────────────────────────────────────
export default function Materiels() {
  const { isReadOnly, activeSociete, activeAgences } = useSociete();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [searchParams] = useSearchParams();
  const [materiels, setMateriels] = useState([]);
  const [stats, setStats] = useState(null);
  const [familles, setFamilles] = useState([]);
  const [sousFamilles, setSousFamilles] = useState([]);
  const [marques, setMarques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tokens, setTokens] = useState([]);
  const [debouncedTokensJSON, setDebouncedTokensJSON] = useState('[]');
  const [contextKey, setContextKey] = useState(0); // s'incrémente quand la société/agence change
  const [filterStatuts, setFilterStatuts] = useState([]);
  const [filterBadges, setFilterBadges] = useState([]);
  const [filterFamilles, setFilterFamilles] = useState([]);
  const [filterSousFamilles, setFilterSousFamilles] = useState([]);
  const [filterMarques, setFilterMarques] = useState([]);
  const [filterParcs, setFilterParcs] = useState([]);
  const [filterComptage, setFilterComptage] = useState([]);
  const [showPanel, setShowPanel] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editingMateriel, setEditingMateriel] = useState(null);
  const [sortField, setSortField] = useState('numero_parc');
  const [sortDir, setSortDir] = useState('asc');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const selectAllRef = useRef(null);
  // Pagination côté serveur
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [fetchingMateriels, setFetchingMateriels] = useState(false);
  const metadataLoadedRef = useRef(false);
  const [metadataReady, setMetadataReady] = useState(false);
  const searchTimerRef = useRef(null);
  // Column visibility
  const [visibleCols, setVisibleCols] = useState(loadSavedCols);
  const [showColMenu, setShowColMenu] = useState(false);
  const colMenuRef = useRef(null);
  // Articles for inline table picker
  const [tableArticles, setTableArticles] = useState([]);

  const handleSort = (field) => {
    setSortDir(prev => sortField === field && prev === 'asc' ? 'desc' : 'asc');
    setSortField(field);
  };

  const toggleCol = (key) => setVisibleCols(v => {
    const next = { ...v, [key]: !v[key] };
    localStorage.setItem(COLS_KEY, JSON.stringify(next));
    return next;
  });

  // Close column menu on outside click
  useEffect(() => {
    const h = (e) => { if (colMenuRef.current && !colMenuRef.current.contains(e.target)) setShowColMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Load articles for inline table picker (type matériel only)
  useEffect(() => {
    api.getArticles({ type: 'materiel', slim: true })
      .then(r => setTableArticles(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  // Ref pour accéder à editingMateriel dans loadData sans en dépendre
  const editingMaterielRef = useRef(null);
  useEffect(() => { editingMaterielRef.current = editingMateriel; }, [editingMateriel]);

  // Vider la sélection quand les données changent
  useEffect(() => { setSelectedIds(new Set()); }, [materiels]);

  // ── Debounce les jetons de recherche ─────────────────────────────────────
  useEffect(() => {
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedTokensJSON(JSON.stringify(tokens)), 400);
    return () => clearTimeout(searchTimerRef.current);
  }, [tokens]);

  // ── Réinitialiser la page quand les filtres/recherche changent ────────────
  useEffect(() => {
    setPage(1);
  }, [contextKey, debouncedTokensJSON, filterStatuts, filterBadges, filterFamilles, filterSousFamilles, filterMarques, filterParcs, sortField, sortDir]);

  // ── Chargement des métadonnées (une seule fois au montage) ────────────────
  const loadMeta = useCallback(async () => {
    try {
      const [statsRes, famsRes, sfamsRes, mrqRes] = await Promise.all([
        api.getMaterielsStats(),
        api.getFamilles({ type: 'materiel' }),
        api.getSousFamilles(),
        api.getMarques(),
      ]);
      setStats(statsRes.data);
      setFamilles(famsRes.data || []);
      setSousFamilles(sfamsRes.data || []);
      setMarques(mrqRes.data);
      metadataLoadedRef.current = true;
      setMetadataReady(true);
      setContextKey(k => k + 1); // Force le rechargement de la liste
    } catch {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [activeSociete?.societe_id, activeAgences.map(a => a.agence_id).join(',')]); // eslint-disable-line

  // ── Chargement paginé des matériels ──────────────────────────────────────
  const fetchMateriels = useCallback(async (pg) => {
    if (!metadataLoadedRef.current) return;
    const currentPage = pg ?? page;
    setFetchingMateriels(true);
    try {
      const sortMap = { compteur: 'compteur_horaire', article: 'article' };
      const params = {
        page: currentPage,
        limit: pageSize,
        sort_by: sortMap[sortField] || sortField,
        sort_dir: sortDir === 'asc' ? 1 : -1,
      };
      // Paramètres issus des jetons de recherche
      const parsedTokens = JSON.parse(debouncedTokensJSON);
      const tokenParams = buildParamsFromTokens(parsedTokens);
      Object.assign(params, tokenParams);

      if (filterStatuts.length)      params.statuts          = filterStatuts.join(',');
      if (filterBadges.length)       params.badges           = filterBadges.join(',');
      if (filterFamilles.length)     params.famille_ids      = filterFamilles.join(',');
      if (filterSousFamilles.length) params.sous_famille_ids = filterSousFamilles.join(',');
      if (filterMarques.length)      params.marque_ids       = filterMarques.join(',');
      if (filterParcs.length)        params.parcs            = filterParcs.join(',');
      if (filterComptage.length)     params.comptage_statuts = filterComptage.join(',');

      const res = await api.getMateriels(params);
      const data = res.data;
      setMateriels(Array.isArray(data.items) ? data.items : []);
      setTotal(data.total || 0);
      setTotalPages(data.pages || 1);
    } catch {
      toast.error('Erreur lors du chargement');
    } finally {
      setFetchingMateriels(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, debouncedTokensJSON, sortField, sortDir, filterStatuts, filterBadges, filterFamilles, filterSousFamilles, filterMarques, filterParcs, filterComptage]);

  // ── Déclenchement initial et sur changements ──────────────────────────────
  useEffect(() => { loadMeta(); }, [loadMeta]);

  useEffect(() => {
    fetchMateriels();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextKey, page, debouncedTokensJSON, sortField, sortDir, filterStatuts, filterBadges, filterFamilles, filterSousFamilles, filterMarques, filterParcs, filterComptage]);

  // ── Deep-link : ?materiel=<id> → ouvre directement la fiche ──────────────
  useEffect(() => {
    const targetId = searchParams.get('materiel');
    if (!targetId) return;
    api.getMateriel(targetId)
      .then(r => { if (r.data) { setEditingMateriel(r.data); setShowPanel(true); } })
      .catch(() => {});
  }, [searchParams]);

  // ── loadData (utilisé par onRefresh, handleSave, handleDelete) ────────────
  const loadData = useCallback(async () => {
    try {
      const [statsRes] = await Promise.all([
        api.getMaterielsStats(),
        fetchMateriels(),
      ]);
      setStats(statsRes.data);
      // Mettre à jour editingMateriel si le panel est ouvert
      if (editingMaterielRef.current) {
        api.getMateriel(editingMaterielRef.current.materiel_id)
          .then(r => { if (r.data) setEditingMateriel(r.data); })
          .catch(() => {});
      }
    } catch {
      toast.error('Erreur lors du chargement');
    }
  }, [fetchMateriels]);

  const handleSave = async (data) => {
    if (editingMateriel) {
      await api.updateMateriel(editingMateriel.materiel_id, data);
      toast.success('Matériel mis à jour');
    } else {
      await api.createMateriel(data);
      toast.success('Matériel créé');
    }
    setEditingMateriel(null);
    loadData();
  };

  const handleDelete = async (materiel) => {
    if (!window.confirm(`Supprimer "${materiel.nom}" ?`)) return;
    try {
      await api.deleteMateriel(materiel.materiel_id);
      toast.success('Matériel supprimé');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleExport = async () => {
    const toExport = selectedIds.size > 0
      ? materiels.filter(m => selectedIds.has(m.materiel_id))
      : materiels;

    // Charger tous les financements pour construire un index par matériel
    let finMap = {};
    try {
      const finRes = await api.getFinancements();
      const fins = Array.isArray(finRes.data) ? finRes.data : [];
      fins.forEach(f => {
        (f.materiel_ids || []).forEach(mid => {
          if (!finMap[mid]) finMap[mid] = f; // 1 financement actif par matériel
        });
      });
    } catch (e) { /* silently ignore */ }

    const headers = [
      'N° Parc', 'Immatriculation', 'Nom', 'Marque', 'Modèle', 'Année', 'N° Série',
      'Statut', 'Compteur (h)', 'N° Immobilisation',
      'N° Contrat', 'Type de contrat', 'Organisme de crédit', 'Date de début', 'Date de fin',
    ];
    const rows = toExport.map(m => {
      const f = finMap[m.materiel_id] || {};
      return [
        m.numero_parc || m.reference || '', m.immatriculation || '',
        m.nom || '', m.marque || '', m.modele || '',
        m.annee || '', m.numero_serie || '',
        STATUTS[m.statut]?.label || m.statut || '',
        m.compteur_horaire ?? '',
        m.numero_immobilisation || '',
        f.numero_contrat || '',
        f.type || '',
        f.entreprise_nom || '',
        f.date_debut || '',
        f.date_fin || '',
      ];
    });
    const csv = [headers, ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `materiels_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${toExport.length} matériel(s) exporté(s)`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full"
          style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  // filterComptage est maintenant géré côté serveur — pas de filtrage client-side
  const displayedMateriels = materiels;

  const materielFamilleIds = new Set(familles.map(f => f.famille_id));
  const sousFamillesForMateriels = sousFamilles.filter(sf => materielFamilleIds.has(sf.famille_id));
  const sousFamillesFiltered = filterFamilles.length > 0
    ? sousFamilles.filter(sf => filterFamilles.includes(sf.famille_id))
    : sousFamillesForMateriels;

  const hasActiveFilters = filterStatuts.length || filterBadges.length || filterFamilles.length || filterSousFamilles.length || filterMarques.length || filterParcs.length || filterComptage.length || tokens.length > 0;

  return (
    <div className="flex flex-col h-full" style={{ gap: '1rem' }}>
      <ReadOnlyBanner />
      {/* Header */}
      <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--primary-solid)',  }}>
            Parc Matériel
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Gérez votre inventaire de matériels</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowImport(true)} data-testid="import-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border"
            style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}>
            <Upload size={16} /> Importer
          </button>
          <button onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border"
            style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}>
            <Download size={16} />
            {selectedIds.size > 0 ? `Exporter (${selectedIds.size})` : 'Exporter'}
          </button>
          <button onClick={() => { setEditingMateriel(null); setShowPanel(true); }}
            data-testid="add-materiel-btn"
            disabled={isReadOnly}
            title={isReadOnly ? 'Mode lecture seule : plusieurs agences sélectionnées' : undefined}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: 'var(--primary-gradient)' }}>
            {isReadOnly ? <Lock size={16} /> : <Plus size={16} />} Nouveau
          </button>
        </div>
      </div>

      {/* Stats — cards filtrantes par parc */}
      {stats && (
        <div className="flex-shrink-0 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {/* Card Total */}
          <StatCard title="Total" value={stats.total} color="var(--primary-solid)" icon={Package}
            active={filterParcs.length === 0}
            onClick={() => setFilterParcs([])}
          />
          {/* Cards par parc dans l'ordre souhaité */}
          {[
            { key: 'client',               title: 'Client',               color: '#15803D', icon: Users },
            { key: 'stock',                title: 'Stock',                color: '#475569', icon: Archive },
            { key: 'location_courte_duree',title: 'Location C.D.',        color: '#1D4ED8', icon: Clock },
            { key: 'vehicule_de_societe',  title: 'Véh. société',         color: '#D97706', icon: Truck },
            { key: 'epave',                title: 'Épave',                color: '#DC2626', icon: XCircle },
          ].map(({ key, title, color, icon }) => (
            <StatCard key={key} title={title} value={stats.par_parc?.[key] ?? 0}
              color={color} icon={icon}
              active={filterParcs.includes(key)}
              onClick={() => setFilterParcs(prev =>
                prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]
              )}
            />
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex-shrink-0 flex flex-col sm:flex-row gap-2 flex-wrap">
        <TokenizedSearchBar
          tokens={tokens}
          onTokensChange={setTokens}
          columns={TOKEN_COLUMNS}
          placeholder="Rechercher par nom, N° parc, marque, modèle, série…"
        />
        <FilterDropdown
          testId="filter-etat"
          label="État"
          selected={filterBadges}
          onChange={setFilterBadges}
          options={BADGE_OPTIONS}
        />
        <FilterDropdown
          testId="filter-parc"
          label="Parc"
          selected={filterParcs}
          onChange={setFilterParcs}
          options={Object.entries(PARCS).map(([k, v]) => ({ value: k, label: v.label, dot: v.color }))}
        />
        {familles.length > 0 && (
          <FilterDropdown
            testId="filter-famille"
            label="Famille"
            selected={filterFamilles}
            onChange={(val) => { setFilterFamilles(val); setFilterSousFamilles([]); }}
            options={familles.map(f => ({ value: f.famille_id, label: f.nom }))}
          />
        )}
        {sousFamillesFiltered.length > 0 && (
          <FilterDropdown
            testId="filter-sous-famille"
            label="Sous-famille"
            selected={filterSousFamilles}
            onChange={setFilterSousFamilles}
            options={sousFamillesFiltered.map(sf => ({ value: sf.sous_famille_id, label: sf.nom }))}
            searchable
          />
        )}
        {marques.length > 0 && (
          <FilterDropdown
            testId="filter-marque"
            label="Marque"
            selected={filterMarques}
            onChange={setFilterMarques}
            options={marques.map(m => ({ value: m.marque_id, label: m.nom }))}
            searchable
          />
        )}
        <FilterDropdown
          testId="filter-comptage"
          label="Comptage"
          selected={filterComptage}
          onChange={setFilterComptage}
          options={[
            { value: 'present',    label: 'Présent',       dot: '#16A34A' },
            { value: 'introuvable',label: 'Introuvable',   dot: '#DC2626' },
            { value: 'jamais',     label: 'Jamais compté', dot: '#9CA3AF' },
          ]}
        />
        {hasActiveFilters && (
          <button onClick={() => { setFilterStatuts([]); setFilterBadges([]); setFilterFamilles([]); setFilterSousFamilles([]); setFilterMarques([]); setFilterParcs([]); setFilterComptage([]); setTokens([]); }}
            className="flex items-center gap-1 px-3 py-2.5 rounded-lg text-xs font-medium"
            style={{ color: 'var(--text-muted)' }}>
            <X size={12} /> Réinitialiser
          </button>
        )}
      </div>

      {/* Table */}
      {!fetchingMateriels && displayedMateriels.length === 0 ? (
        <div className="flex-1 min-h-0 bg-white rounded-xl border p-12 text-center" style={{ borderColor: 'var(--border)' }}>
          <Package size={48} className="mx-auto mb-4" style={{ color: 'var(--text-disabled)' }} />
          <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text)',  }}>
            Aucun matériel
          </h3>
          <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
            {hasActiveFilters
              ? 'Aucun résultat pour ces critères'
              : 'Commencez par ajouter votre premier matériel'}
          </p>
          {!hasActiveFilters && (
            <div className="flex justify-center gap-3">
              <button onClick={() => setShowImport(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border"
                style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}>
                <Upload size={16} /> Importer un CSV
              </button>
              <button onClick={() => { setEditingMateriel(null); setShowPanel(true); }}
                disabled={isReadOnly}
                title={isReadOnly ? 'Mode lecture seule : plusieurs agences sélectionnées' : undefined}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'var(--primary-gradient)' }}>
                {isReadOnly ? <Lock size={16} /> : <Plus size={16} />} Ajouter manuellement
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col" style={{ gap: '0.5rem' }}>
          <div className="flex-1 min-h-0 bg-white rounded-xl border flex flex-col overflow-hidden" style={{ borderColor: 'var(--border)' }}>
            <div className="flex-1 min-h-0 overflow-x-auto overflow-y-auto">
              <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '2%' }} />
                  {COLUMN_DEFS.filter(c => visibleCols[c.key] !== false).map(c => (
                    <col key={c.key} style={{ width: `${c.width}%` }} />
                  ))}
                  <col style={{ width: '2%' }} />
                </colgroup>
                <thead className="sticky top-0 z-10">
                  <tr className="border-b" style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
                    {/* Select-all checkbox */}
                    <th className="pl-2 pr-1 py-3 overflow-hidden">
                      <input
                        ref={selectAllRef}
                        type="checkbox"
                        checked={selectedIds.size === displayedMateriels.length && displayedMateriels.length > 0}
                        onChange={() => {
                          const allSel = selectedIds.size === displayedMateriels.length;
                          setSelectedIds(allSel ? new Set() : new Set(displayedMateriels.map(m => m.materiel_id)));
                        }}
                        className="w-4 h-4 rounded cursor-pointer"
                        style={{ accentColor: 'var(--primary-solid)' }}
                      />
                    </th>
                    {visibleCols.numero_parc     && <ThSort label="N° Série / Immat. / N° Parc" field="numero_serie" sortField={sortField} sortDir={sortDir} onSort={handleSort} />}
                    {visibleCols.article         && <ThSort label="Article"               field="article"          sortField={sortField} sortDir={sortDir} onSort={handleSort} />}
                    {visibleCols.nom             && <ThSort label="Nom / Marque / Modèle"  field="nom"              sortField={sortField} sortDir={sortDir} onSort={handleSort} />}
                    {visibleCols.annee           && <ThSort label="Année"                  field="annee"            sortField={sortField} sortDir={sortDir} onSort={handleSort} />}
                    {visibleCols.badges          && <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap overflow-hidden" style={{ color: 'var(--text-muted)' }}>État</th>}
                    {visibleCols.parc            && <ThSort label="Parc"                   field="parc"             sortField={sortField} sortDir={sortDir} onSort={handleSort} />}
                    {visibleCols.compteur        && <ThSort label="Compteur"               field="compteur"         sortField={sortField} sortDir={sortDir} onSort={handleSort} />}
                    {visibleCols.proprietaire    && <ThSort label="Exploitant / Propriétaire" field="exploitant_nom" sortField={sortField} sortDir={sortDir} onSort={handleSort} />}
                    {visibleCols.dernier_comptage && <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap overflow-hidden" style={{ color: 'var(--text-muted)' }}>Dernier Comptage</th>}
                    {/* Sélecteur de colonnes intégré au tableau */}
                    <th className="px-2 py-3 text-right overflow-hidden" style={{ color: 'var(--text-muted)', background: 'var(--bg-subtle)' }}>
                      <div className="relative flex justify-end" ref={colMenuRef}>
                        <button data-testid="col-picker-btn" onClick={() => setShowColMenu(v => !v)}
                          title="Choisir les colonnes"
                          className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors"
                          style={{ color: 'var(--text-muted)' }}>
                          <SlidersHorizontal size={14} />
                        </button>
                        {showColMenu && (
                          <div className="absolute right-0 top-full mt-1 bg-white rounded-xl border shadow-xl z-40 py-1.5"
                            style={{ borderColor: 'var(--border)', minWidth: '200px' }}>
                            {COLUMN_DEFS.map(col => (
                              <label key={col.key}
                                className={`flex items-center gap-2.5 px-3 py-1.5 ${col.locked ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'}`}>
                                <input type="checkbox" checked={!!visibleCols[col.key]}
                                  onChange={() => { if (!col.locked) toggleCol(col.key); }}
                                  disabled={col.locked}
                                  className="w-3.5 h-3.5 rounded"
                                  style={{ accentColor: 'var(--primary-solid)' }} />
                                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{col.label}</span>
                                {col.locked && <span className="ml-auto text-xs" style={{ color: 'var(--text-disabled)' }}>fixe</span>}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {fetchingMateriels ? (
                    <tr>
                      <td colSpan={20} className="py-12 text-center">
                        <div className="flex items-center justify-center gap-2" style={{ color: 'var(--text-muted)' }}>
                          <span className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
                            style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
                          <span className="text-sm">Chargement…</span>
                        </div>
                      </td>
                    </tr>
                  ) : displayedMateriels.map(m => (
                    <MaterielRow key={m.materiel_id} materiel={m}
                      selected={selectedIds.has(m.materiel_id)}
                      onToggle={() => setSelectedIds(prev => {
                        const next = new Set(prev);
                        next.has(m.materiel_id) ? next.delete(m.materiel_id) : next.add(m.materiel_id);
                        return next;
                      })}
                      onOpen={(mat) => { setEditingMateriel(mat); setShowPanel(true); }}
                      articles={tableArticles}
                      visibleCols={visibleCols}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2.5 border-t text-xs flex items-center justify-between gap-3" style={{ borderColor: 'var(--border)', color: 'var(--text-placeholder)' }}>
              <div className="flex items-center gap-3">
                <span data-testid="materiels-count">{total} matériel{total !== 1 ? 's' : ''}</span>
                {selectedIds.size > 0 && (
                  <span style={{ color: 'var(--primary-solid)' }}>
                    · {selectedIds.size} sélectionné{selectedIds.size !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              {totalPages > 1 && (
                <div className="flex items-center gap-1" data-testid="pagination-controls">
                  <button
                    data-testid="pagination-prev"
                    disabled={page <= 1}
                    onClick={() => setPage(p => p - 1)}
                    className="px-2.5 py-1 rounded border text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                    style={{ borderColor: 'var(--border)' }}>
                    ‹ Préc
                  </button>
                  <span className="px-2 font-medium" style={{ color: 'var(--text-secondary)' }}>
                    {page} / {totalPages}
                  </span>
                  <button
                    data-testid="pagination-next"
                    disabled={page >= totalPages}
                    onClick={() => setPage(p => p + 1)}
                    className="px-2.5 py-1 rounded border text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                    style={{ borderColor: 'var(--border)' }}>
                    Suiv ›
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Side Panels */}
      <MaterielPanel
        open={showPanel}
        onOpenChange={(open) => { if (!open) { setShowPanel(false); setEditingMateriel(null); } }}
        materiel={editingMateriel}
        onSave={handleSave}
        onDelete={(mat) => { setShowPanel(false); setEditingMateriel(null); handleDelete(mat); }}
        isReadOnly={isReadOnly}
        onRefresh={loadData}
      />
      <ImportPanel
        open={showImport}
        onOpenChange={setShowImport}
        onImportComplete={loadData}
      />
    </div>
  );
}
